<template>
  <v-container fluid class="pa-6">
    <!-- PR-UPGRADE-MODAL: per-tool premium-banner replaced by the one shared
         UpgradeToPlusModal (mounted globally in App.vue). Gating below is
         unchanged; it opens the shared modal via `upgradeModal/open`. -->
    <v-row class="text-start">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-2">
          <h1 class="text-h4 font-weight-bold mb-0 heading-primary">
            Sage
            <span class="text-body-1 text--secondary font-weight-regular"
              >AI Career Coach</span
            >
          </h1>
        </div>
        <div class="d-flex align-center mb-4">
          <v-btn-toggle
            v-model="activeMode"
            mandatory
            dense
            class="mode-toggle"
          >
            <v-btn
              value="capture"
              small
              :color="activeMode === 'capture' ? 'primary' : ''"
            >
              <v-icon small left>mdi-trophy-outline</v-icon>
              Capture Wins
            </v-btn>
            <v-btn
              value="coach"
              small
              :color="activeMode === 'coach' ? 'deep-purple' : ''"
            >
              <v-icon small left>mdi-head-lightbulb-outline</v-icon>
              Career Coach
            </v-btn>
          </v-btn-toggle>
        </div>
        <p
          v-if="activeMode === 'capture'"
          class="text-body-1 text--secondary mb-4"
        >
          Just talk naturally about what you do. Sage will help capture your
          achievements.
        </p>
      </v-col>
    </v-row>

    <v-slide-y-transition>
      <div
        v-if="showCoachingNudge && activeMode === 'capture'"
        class="coaching-nudge-banner mx-3 mb-4"
      >
        <v-card outlined class="pa-3 coaching-nudge-card">
          <div class="d-flex align-center flex-wrap">
            <v-icon color="deep-purple" class="mr-2"
              >mdi-lightbulb-on-outline</v-icon
            >
            <span class="text-body-2 mr-3"
              >It sounds like you're looking for career guidance. Want to switch
              to Coach mode?</span
            >
            <v-chip
              small
              color="deep-purple"
              text-color="white"
              class="mr-2 mb-1"
              style="cursor: pointer;"
              @click="switchToCoachFromNudge"
              >Yes, switch</v-chip
            >
            <v-chip
              small
              outlined
              color="grey"
              class="mb-1"
              style="cursor: pointer;"
              @click="dismissCoachingNudge"
              >No, keep capturing</v-chip
            >
          </div>
        </v-card>
      </div>
    </v-slide-y-transition>

    <v-row v-if="activeMode === 'coach'">
      <v-col cols="12">
        <v-alert
          type="info"
          text
          dense
          icon="mdi-information-outline"
          class="mb-4"
          style="max-width: 720px; margin: 0 auto;"
        >
          <strong>Sage AI Coach disclaimer:</strong> This is not professional career, legal, financial, or medical advice. AI responses are subjective and may be incorrect. Use this coach with caution and consult a qualified professional for important career decisions.
        </v-alert>
        <transition name="coach-fade" mode="out-in">
          <div
            v-if="coachPhase === 'landing'"
            key="landing"
            class="coach-landing"
          >
            <div class="text-center mb-6">
              <div class="coach-avatar-container mb-4">
                <div v-if="userAvatarUrl" class="coach-user-avatar">
                  <img
                    :src="userAvatarUrl"
                    alt="User"
                    class="coach-avatar-img"
                  />
                </div>
                <div v-else class="coach-user-initials">
                  {{ userInitials }}
                </div>
              </div>
              <h2 class="text-h4 font-weight-bold mb-2 heading-primary">
                What would you like to work on?
              </h2>
              <p
                class="text-body-1 text--secondary"
                style="max-width: 520px; margin: 0 auto;"
              >
                Sage knows your profile, skills, and career evidence. Ask
                anything about your career path.
              </p>
            </div>
            <v-row justify="center" class="mb-6">
              <v-col
                v-for="(card, ci) in coachSuggestionCards"
                :key="ci"
                cols="12"
                sm="6"
                md="4"
                lg="4"
              >
                <v-card
                  outlined
                  class="coach-suggestion-card pa-4"
                  @click="startCoachFromCard(card.text)"
                  style="cursor: pointer; height: 100%;"
                >
                  <v-icon :color="card.color" class="mb-2">{{
                    card.icon
                  }}</v-icon>
                  <div class="text-body-2 font-weight-medium heading-primary">
                    {{ card.text }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <div
              class="coach-landing-input d-flex align-center justify-center"
              style="max-width: 600px; margin: 0 auto;"
            >
              <voice-input
                v-model="coachInput"
                placeholder="Ask Sage anything about your career..."
                label=""
                class="mr-2 flex-grow-1"
                @keyup-enter="sendCoachMessage"
                @voice-complete="sendCoachMessage"
              ></voice-input>
              <v-btn
                icon
                color="deep-purple"
                :disabled="!coachInput.trim()"
                @click="sendCoachMessage"
              >
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </div>
          </div>

          <div
            v-else-if="coachPhase === 'chat'"
            key="chat"
            class="coach-chat-container"
          >
            <v-card outlined class="chat-panel-card">
              <v-card-title
                class="d-flex align-center"
                style="background: linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%); color: white;"
              >
                <v-icon small class="mr-2" color="white"
                  >mdi-head-lightbulb-outline</v-icon
                >
                Sage AI Career Coach
                <v-spacer></v-spacer>
                <v-btn x-small text color="white" @click="resetCoachToLanding">
                  <v-icon x-small left>mdi-arrow-left</v-icon>
                  New Topic
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>

              <div class="chat-messages" ref="coachChatMessages">
                <div
                  v-for="(msg, idx) in coachMessages"
                  :key="'coach-' + idx"
                  class="chat-bubble-row"
                  :class="
                    msg.role === 'assistant' ? 'bubble-left' : 'bubble-right'
                  "
                >
                  <div
                    v-if="msg.role === 'assistant'"
                    class="chat-avatar coach-avatar-bubble"
                  >
                    <span class="sage-avatar">S</span>
                  </div>
                  <div
                    class="chat-bubble"
                    :class="
                      msg.role === 'assistant'
                        ? 'bubble-ai coach-bubble-ai'
                        : 'bubble-user coach-bubble-user'
                    "
                    v-html="msg.role === 'assistant' ? renderCoachMarkdown(msg.content) : escapeHtml(msg.content)"
                  ></div>
                </div>
                <div v-if="coachIsThinking" class="chat-bubble-row bubble-left">
                  <div class="chat-avatar coach-avatar-bubble">
                    <span class="sage-avatar">S</span>
                  </div>
                  <div class="chat-bubble bubble-ai coach-bubble-ai">
                    <v-progress-circular
                      indeterminate
                      size="16"
                      width="2"
                      color="deep-purple"
                      class="mr-2"
                    ></v-progress-circular>
                    Thinking...
                  </div>
                </div>

                <div
                  v-if="coachFollowUps.length > 0 && !coachIsThinking"
                  class="coach-followups-row"
                >
                  <v-chip
                    v-for="(fu, fi) in coachFollowUps"
                    :key="'fu-' + fi"
                    small
                    color="deep-purple lighten-4"
                    text-color="deep-purple darken-2"
                    class="mr-2 mb-1"
                    style="cursor: pointer;"
                    @click="sendCoachFollowUp(fu)"
                  >
                    {{ fu }}
                  </v-chip>
                </div>
              </div>

              <v-divider></v-divider>

              <div class="chat-input-bar pa-3">
                <voice-input
                  v-model="coachInput"
                  placeholder="Ask a follow-up... or click the mic to speak"
                  label=""
                  class="chat-text-input flex-grow-1"
                  @keyup-enter="sendCoachMessage"
                  @voice-complete="sendCoachMessage"
                ></voice-input>
                <v-btn
                  icon
                  color="deep-purple"
                  class="ml-2"
                  :disabled="!coachInput.trim() || coachIsThinking"
                  @click="sendCoachMessage"
                >
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>
            </v-card>
          </div>
        </transition>
      </v-col>
    </v-row>

    <!-- PHASE: Chat — Two-panel layout -->
    <v-row v-if="phase === 'chat' && activeMode === 'capture'">
      <v-col cols="12" md="3">
        <v-card
          class="pa-3 mb-3"
          color="primary"
          dark
          style="border-radius: 12px;"
        >
          <div class="d-flex align-center">
            <v-icon size="28" class="mr-2">mdi-head-lightbulb-outline</v-icon>
            <div>
              <div class="text-subtitle-1 font-weight-bold">Sage</div>
              <div class="text-caption opacity-80">{{ statusLabel }}</div>
            </div>
          </div>
        </v-card>

        <v-btn
          v-if="userMessageCount > 0 && sageMode !== 'star-gaps'"
          :color="readyToExtract ? 'success' : 'primary'"
          block
          class="mb-3"
          @click="extractAchievements"
          :loading="isExtracting"
          :outlined="!readyToExtract"
          style="border-radius: 8px;"
        >
          <v-icon left>{{
            readyToExtract ? "mdi-check-all" : "mdi-content-save-outline"
          }}</v-icon>
          {{ readyToExtract ? "Save My Achievements" : "I'm Done" }}
        </v-btn>

        <v-card
          v-if="sessionTotal > 0"
          class="pa-3 mb-3"
          elevation="1"
          style="border-radius: 10px;"
        >
          <div class="d-flex align-center">
            <v-icon
              small
              :color="sessionTotal >= 3 ? 'success' : 'amber'"
              class="mr-2"
            >
              {{ sessionTotal >= 3 ? "mdi-trophy" : "mdi-star-outline" }}
            </v-icon>
            <span class="text-body-2 font-weight-medium"
              >{{ sessionTotal }} captured</span
            >
            <v-spacer></v-spacer>
            <span
              class="text-caption"
              :class="sessionTotal >= 3 ? 'success--text' : 'text--secondary'"
            >
              {{ sessionTotal >= 3 ? "Goal met!" : "Goal: 3" }}
            </span>
          </div>
          <v-progress-linear
            :value="Math.min(sessionTotal / 3, 1) * 100"
            :color="sessionTotal >= 3 ? 'success' : 'amber'"
            height="4"
            rounded
            class="mt-2"
          ></v-progress-linear>
        </v-card>

        <v-card
          v-if="hasJobs && !(sageMode === 'star-gaps' && currentGapItem)"
          class="pa-3 mb-3"
          elevation="1"
          style="border-radius: 10px;"
        >
          <div class="text-subtitle-2 font-weight-medium mb-2">
            <v-icon x-small class="mr-1">mdi-link-variant</v-icon>
            Link to Role
          </div>
          <v-select
            v-model="selectedJobKey"
            :items="jobOptionsForSelect"
            item-text="label"
            item-value="key"
            placeholder="Select a role (optional)"
            dense
            outlined
            hide-details
            clearable
            class="mb-1"
          ></v-select>
        </v-card>

        <v-card
          v-if="userMessageCount > 0"
          class="pa-3 mb-3"
          elevation="1"
          style="border-radius: 10px;"
        >
          <div class="text-subtitle-2 font-weight-medium mb-2">
            <v-icon x-small class="mr-1">mdi-target</v-icon>
            STAR Progress
          </div>
          <div
            class="d-flex align-center mb-1"
            v-for="item in starItems"
            :key="item.key"
          >
            <v-icon
              x-small
              :color="captured[item.key] ? 'success' : 'grey lighten-1'"
              class="mr-2"
            >
              {{
                captured[item.key] ? "mdi-check-circle" : "mdi-circle-outline"
              }}
            </v-icon>
            <span
              class="text-caption"
              :class="captured[item.key] ? 'success--text' : 'grey--text'"
              >{{ item.label }}</span
            >
          </div>
          <v-progress-linear
            :value="capturedCount * 25"
            color="success"
            height="4"
            rounded
            class="mt-2"
          ></v-progress-linear>
        </v-card>

        <div class="d-flex align-center mb-3">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on" class="d-flex align-center">
                <v-switch
                  v-model="voiceReadbackEnabled"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                  @change="saveVoicePreference"
                ></v-switch>
                <v-icon
                  x-small
                  class="mr-1 ml-1"
                  :color="voiceReadbackEnabled ? 'primary' : 'grey'"
                >
                  {{
                    voiceReadbackEnabled ? "mdi-volume-high" : "mdi-volume-off"
                  }}
                </v-icon>
                <span class="text-caption">AI Voice</span>
              </div>
            </template>
            <span>{{
              voiceReadbackEnabled ? "AI Voice On" : "Enable AI Voice"
            }}</span>
          </v-tooltip>
          <v-btn
            v-if="isSpeaking"
            icon
            x-small
            @click="stopSpeaking"
            class="ml-1"
          >
            <v-icon small color="error">mdi-stop</v-icon>
          </v-btn>
        </div>

        <v-btn
          text
          small
          block
          @click="$router.push('/SmallWinsCapture')"
          class="text--secondary"
        >
          <v-icon small left>mdi-arrow-left</v-icon>
          Back to Capture
        </v-btn>
      </v-col>

      <v-col cols="12" md="9">
        <v-card outlined class="chat-panel-card">
          <v-card-title class="d-flex align-center">
            <v-icon small class="mr-2">mdi-chat-outline</v-icon>
            Conversation
            <v-spacer></v-spacer>
            <span class="text-caption text--secondary"
              >{{ userMessageCount }} message{{
                userMessageCount !== 1 ? "s" : ""
              }}</span
            >
          </v-card-title>
          <v-divider></v-divider>

          <div
            v-if="
              (gapPhase === 'filling' ||
                gapPhase === 'confirm-save' ||
                gapPhase === 'review') &&
                currentGapItem
            "
            class="gap-context-banner pa-3"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <v-icon small color="amber darken-2" class="mr-1"
                  >mdi-target</v-icon
                >
                <span class="text-caption font-weight-medium">{{
                  gapPhase === "review" ? "Review & Save" : "Filling STAR Gaps"
                }}</span>
                <span class="text-caption text--secondary ml-2"
                  >({{ gapQueueIndex + 1 }} of {{ gapQueue.length }})</span
                >
                <v-chip
                  x-small
                  class="ml-2"
                  :color="
                    currentGapItem && currentGapItem.source === 'work'
                      ? 'blue lighten-4'
                      : 'green lighten-4'
                  "
                  :text-color="
                    currentGapItem && currentGapItem.source === 'work'
                      ? 'blue darken-2'
                      : 'green darken-2'
                  "
                  >{{
                    currentGapItem && currentGapItem.source === "work"
                      ? "Work Experience"
                      : "Quick Win"
                  }}</v-chip
                >
              </div>
              <div v-if="gapPhase !== 'review'">
                <v-btn
                  x-small
                  text
                  color="grey"
                  @click="skipGapItem"
                  :disabled="isThinking"
                  >Skip</v-btn
                >
                <v-btn x-small text color="error" @click="stopGapFill"
                  >Stop</v-btn
                >
              </div>
            </div>
            <div
              v-if="currentGapItem.company"
              class="text-caption font-weight-medium mt-1 heading-primary"
            >
              <v-icon x-small class="mr-1">mdi-briefcase</v-icon
              >{{ currentGapItem.company
              }}{{ currentGapItem.role ? " — " + currentGapItem.role : "" }}
            </div>
            <div
              class="text-body-2 mt-1"
              style="max-height: 40px; overflow: hidden; text-overflow: ellipsis;"
            >
              {{ currentGapItem.text || "(untitled achievement)" }}
            </div>
            <div
              v-if="gapPhase !== 'review'"
              class="text-caption text--secondary mt-1"
            >
              Missing: {{ currentGapMissingLabels }}
            </div>
          </div>

          <div class="chat-messages" ref="chatMessages">
            <div
              v-if="userMessageCount === 0 && !isThinking"
              class="sage-starter-chips pa-3"
            >
              <div class="text-caption text--secondary mb-2">
                Or try one of these:
              </div>
              <v-chip
                small
                color="purple lighten-4"
                text-color="purple darken-2"
                class="mr-1 mb-1"
                style="cursor: pointer;"
                @click="startSageMode('review-wins')"
              >
                <v-icon left x-small>mdi-trophy-outline</v-icon>Review My Wins
              </v-chip>
              <v-chip
                small
                color="orange lighten-4"
                text-color="orange darken-3"
                class="mr-1 mb-1"
                style="cursor: pointer;"
                @click="startStarGapFill()"
              >
                <v-icon left x-small>mdi-target</v-icon>Fill STAR Gaps
              </v-chip>
              <v-chip
                small
                color="teal lighten-4"
                text-color="teal darken-2"
                class="mr-1 mb-1"
                style="cursor: pointer;"
                @click="startSageMode('update')"
              >
                <v-icon left x-small>mdi-refresh</v-icon>Update My Story
              </v-chip>
              <v-divider
                vertical
                class="mx-2 mb-1"
                style="display: inline-block; height: 20px; vertical-align: middle;"
              ></v-divider>
              <v-chip
                small
                color="blue lighten-4"
                text-color="blue darken-2"
                class="mr-1 mb-1"
                style="cursor: pointer;"
                @click="switchToCoachWithTopic('roles')"
              >
                <v-icon left x-small>mdi-compass-outline</v-icon>What Roles Fit
                Me?
              </v-chip>
              <v-chip
                small
                color="amber lighten-4"
                text-color="amber darken-3"
                class="mr-1 mb-1"
                style="cursor: pointer;"
                @click="switchToCoachWithTopic('strengthen')"
              >
                <v-icon left x-small>mdi-arm-flex</v-icon>Strengthen My Profile
              </v-chip>
              <v-chip
                small
                color="green lighten-4"
                text-color="green darken-2"
                class="mr-1 mb-1"
                style="cursor: pointer;"
                @click="switchToCoachWithTopic('learn')"
              >
                <v-icon left x-small>mdi-book-open-variant</v-icon>What Should I
                Learn?
              </v-chip>
            </div>
            <div
              v-for="(msg, idx) in messages"
              :key="idx"
              class="chat-bubble-row"
              :class="msg.role === 'assistant' ? 'bubble-left' : 'bubble-right'"
            >
              <div v-if="msg.role === 'assistant'" class="chat-avatar">
                <span class="sage-avatar">S</span>
              </div>
              <div
                class="chat-bubble"
                :class="msg.role === 'assistant' ? 'bubble-ai' : 'bubble-user'"
              >
                {{ msg.content }}
              </div>
            </div>
            <div v-if="isThinking" class="chat-bubble-row bubble-left">
              <div class="chat-avatar">
                <span class="sage-avatar">S</span>
              </div>
              <div class="chat-bubble bubble-ai">
                <v-progress-circular
                  indeterminate
                  size="16"
                  width="2"
                  color="primary"
                  class="mr-2"
                ></v-progress-circular>
                Thinking...
              </div>
            </div>

            <div v-if="showActionChips && !isThinking" class="action-chips-row">
              <v-chip
                v-if="readyToExtract"
                color="success"
                text-color="white"
                class="mr-2 mb-1 action-chip"
                @click="extractAchievements"
                :disabled="isExtracting"
              >
                <v-icon small left>mdi-check-all</v-icon>
                Save My Story
              </v-chip>
              <v-chip
                v-if="readyToExtract"
                color="primary"
                outlined
                class="mr-2 mb-1 action-chip"
                @click="dismissChips"
              >
                <v-icon small left>mdi-chat-plus</v-icon>
                Keep Going
              </v-chip>
              <v-chip
                v-if="
                  !readyToExtract &&
                    userMessageCount >= 2 &&
                    sageMode !== 'star-gaps'
                "
                color="primary"
                outlined
                class="mr-2 mb-1 action-chip"
                @click="extractAchievements"
                :disabled="isExtracting"
              >
                <v-icon small left>mdi-content-save-outline</v-icon>
                Done — Save What You Have
              </v-chip>
              <v-chip
                v-if="
                  sageMode === 'star-gaps' &&
                    gapPhase === 'filling' &&
                    gapUserExchangeCount >= 2
                "
                color="success"
                text-color="white"
                class="mr-2 mb-1 action-chip"
                @click="forceGapSave"
                :disabled="isSaving"
              >
                <v-icon small left>mdi-content-save-outline</v-icon>
                Done — Save This Item
              </v-chip>
              <v-chip
                v-if="gapPhase === 'confirm-save'"
                color="success"
                text-color="white"
                class="mr-2 mb-1 action-chip"
                @click="enterGapReview"
                :disabled="isSaving"
              >
                <v-icon small left>mdi-eye</v-icon>
                Review & Save
              </v-chip>
              <v-chip
                v-if="gapPhase === 'confirm-save'"
                color="grey"
                outlined
                class="mr-2 mb-1 action-chip"
                @click="skipGapItem"
              >
                <v-icon small left>mdi-skip-next</v-icon>
                Skip This One
              </v-chip>
              <v-chip
                v-if="credentialKeywordDetected"
                color="cyan lighten-4"
                text-color="cyan darken-3"
                class="mr-2 mb-1 action-chip"
                @click="$router.push('/Organizations')"
              >
                <v-icon small left>mdi-certificate</v-icon>
                Manage Credentials
              </v-chip>
            </div>
          </div>

          <div v-if="gapPhase === 'review'" class="gap-review-panel pa-4">
            <div class="text-subtitle-2 font-weight-medium mb-3">
              <v-icon small class="mr-1" color="primary"
                >mdi-pencil-outline</v-icon
              >
              Review the STAR elements below. Edit if needed, then save.
            </div>
            <v-textarea
              v-model="gapReviewFields.summary"
              label="Summary Bullet"
              placeholder="A polished summary of this accomplishment"
              rows="2"
              auto-grow
              outlined
              dense
              class="mb-1"
              hide-details
            ></v-textarea>
            <div
              v-if="
                gapOriginalSummary &&
                  gapReviewFields.summary !== gapOriginalSummary
              "
              class="mb-3"
            >
              <a
                class="text-caption primary--text"
                style="cursor: pointer;"
                @click="gapReviewFields.summary = gapOriginalSummary"
              >
                <v-icon x-small color="primary" class="mr-1">mdi-undo</v-icon>
                Revert to original
              </a>
            </div>
            <div v-else class="mb-3"></div>
            <v-textarea
              v-model="gapReviewFields.background"
              label="Context / Situation"
              placeholder="What was the background or challenge?"
              rows="2"
              auto-grow
              outlined
              dense
              class="mb-2"
              hide-details
            ></v-textarea>
            <v-textarea
              v-model="gapReviewFields.responsibility"
              label="Task / Responsibility"
              placeholder="What was your specific task or responsibility?"
              rows="2"
              auto-grow
              outlined
              dense
              class="mb-2"
              hide-details
            ></v-textarea>
            <v-textarea
              v-model="gapReviewFields.actionResult"
              label="Action / What You Did"
              placeholder="What specific actions did you take?"
              rows="2"
              auto-grow
              outlined
              dense
              class="mb-2"
              hide-details
            ></v-textarea>
            <v-textarea
              v-model="gapReviewFields.measurement"
              label="Result / Impact"
              placeholder="What was the measurable outcome?"
              rows="2"
              auto-grow
              outlined
              dense
              class="mb-3"
              hide-details
            ></v-textarea>
            <div class="d-flex flex-wrap">
              <v-btn
                color="success"
                class="mr-2 mb-2"
                :loading="isSaving"
                @click="saveFromReview('continue')"
              >
                <v-icon left small>mdi-check</v-icon>
                Save & Next Item
              </v-btn>
              <v-btn
                color="primary"
                class="mr-2 mb-2"
                :loading="isSaving"
                @click="saveFromReview('profile')"
              >
                <v-icon left small>mdi-account-outline</v-icon>
                Save & Go to Profile
              </v-btn>
              <v-btn outlined class="mb-2" @click="gapPhase = 'filling'">
                <v-icon left small>mdi-arrow-left</v-icon>
                Back to Chat
              </v-btn>
            </div>
          </div>

          <v-divider></v-divider>

          <div v-if="gapPhase !== 'review'" class="chat-input-bar pa-3">
            <voice-input
              ref="chatInput"
              v-model="userInput"
              :multiline="true"
              :rows="1"
              label=""
              placeholder="Type or speak..."
              hide-details
              dense
              class="chat-text-input"
              :enter-submits="true"
              @voice-complete="sendMessage"
              @submit="sendMessage"
            ></voice-input>
            <v-btn
              icon
              color="primary"
              class="ml-2"
              :disabled="!userInput.trim() || isThinking"
              @click="sendMessage"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- PHASE: Review extracted achievements — Two-panel layout -->
    <v-row v-if="phase === 'review' && activeMode === 'capture'">
      <v-col cols="12" md="3">
        <v-card class="pa-4 mb-4" color="success" dark>
          <v-icon size="36" class="mb-2">mdi-check-circle</v-icon>
          <div class="text-h6 mb-1">Review</div>
          <div class="text-caption opacity-80">
            {{ extractedAchievements.length }} achievement{{
              extractedAchievements.length !== 1 ? "s" : ""
            }}
            captured
          </div>
        </v-card>

        <v-card class="pa-3 mb-3" elevation="1">
          <div class="text-subtitle-2 font-weight-medium mb-3">Actions</div>

          <v-btn
            color="primary"
            block
            class="mb-2"
            :loading="isSaving"
            @click="saveAllAchievements"
            :disabled="extractedAchievements.length === 0"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save All ({{ extractedAchievements.length }})
          </v-btn>

          <v-btn outlined block @click="phase = 'chat'">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Conversation
          </v-btn>
        </v-card>
      </v-col>

      <v-col cols="12" md="9">
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <v-icon small class="mr-2">mdi-eye</v-icon>
            Review Your Achievements
            <v-spacer></v-spacer>
            <span class="text-caption text--secondary"
              >Edit anything that doesn't look right</span
            >
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-4">
            <div
              v-for="(ach, idx) in extractedAchievements"
              :key="'ach-' + idx"
              class="achievement-review-card pa-4 mb-4"
            >
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-subtitle-2 font-weight-medium"
                  >Achievement {{ idx + 1 }}</span
                >
                <v-btn
                  icon
                  x-small
                  color="error"
                  @click="removeAchievement(idx)"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <v-text-field
                v-model="ach.summary"
                label="Summary"
                outlined
                dense
                hide-details
                class="mb-3"
              ></v-text-field>
              <v-textarea
                v-model="ach.situation"
                label="Situation / Context"
                auto-grow
                :rows="2"
                outlined
                dense
                hide-details
                class="mb-2"
              ></v-textarea>
              <v-textarea
                v-model="ach.task"
                label="Task / Responsibility"
                auto-grow
                :rows="2"
                outlined
                dense
                hide-details
                class="mb-2"
              ></v-textarea>
              <v-textarea
                v-model="ach.action"
                label="Action / What You Did"
                auto-grow
                :rows="2"
                outlined
                dense
                hide-details
                class="mb-2"
              ></v-textarea>
              <v-textarea
                v-model="ach.result"
                label="Result / Impact"
                auto-grow
                :rows="2"
                outlined
                dense
                hide-details
                class="mb-2"
              ></v-textarea>
            </div>

            <div
              v-if="extractedAchievements.length === 0"
              class="text-center py-4 text--secondary"
            >
              No achievements were extracted. You can go back and tell me more,
              or save the conversation as-is.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- PHASE: Done -->
    <v-row v-if="phase === 'done' && activeMode === 'capture'">
      <v-col cols="12" md="3">
        <v-card class="pa-4 mb-4" color="success" dark>
          <v-icon size="36" class="mb-2">mdi-check-circle-outline</v-icon>
          <div class="text-h6 mb-1">Saved!</div>
          <div class="text-caption opacity-80">
            {{ savedCount }} achievement{{ savedCount !== 1 ? "s" : "" }}
          </div>
        </v-card>

        <v-card class="pa-3 mb-3" outlined>
          <div class="text-subtitle-2 font-weight-medium mb-2">
            Session Progress
          </div>
          <div class="d-flex align-center justify-center mb-2">
            <span
              class="text-h4 font-weight-bold"
              :class="sessionTotal >= 3 ? 'success--text' : 'primary--text'"
              >{{ sessionTotal }}</span
            >
          </div>
          <div class="text-caption text--secondary text-center mb-2">
            achievement{{ sessionTotal !== 1 ? "s" : "" }} this session
          </div>
          <v-progress-linear
            :value="Math.min(sessionTotal / 3, 1) * 100"
            :color="sessionTotal >= 3 ? 'success' : 'primary'"
            height="6"
            rounded
          ></v-progress-linear>
          <div
            class="text-caption text-center mt-1"
            :class="sessionTotal >= 3 ? 'success--text' : 'text--secondary'"
          >
            {{
              sessionTotal >= 3
                ? "Goal reached!"
                : 3 - sessionTotal + " more to reach your goal"
            }}
          </div>
        </v-card>

        <v-card class="pa-3" outlined>
          <v-btn color="primary" block class="mb-2" @click="startAnother">
            <v-icon left>mdi-plus</v-icon>
            Capture More
          </v-btn>
          <v-btn outlined block @click="$router.push('/SmallWinsCapture')">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Captures
          </v-btn>
        </v-card>
      </v-col>

      <v-col cols="12" md="9">
        <v-card class="pa-6 text-center" outlined>
          <v-icon size="56" color="success" class="mb-3"
            >mdi-check-circle-outline</v-icon
          >
          <h2 class="mb-2">
            {{ savedCount }} Achievement{{ savedCount !== 1 ? "s" : "" }} Saved!
          </h2>
          <p class="text-body-1 text--secondary mb-0">
            Your accomplishments have been captured and saved to your career
            evidence.
          </p>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showCelebration" max-width="480" persistent>
      <v-card class="celebration-card text-center pa-6">
        <div class="celebration-emoji mb-3">&#127881;</div>
        <h2 class="text-h5 font-weight-bold mb-2 heading-primary">
          Hat Trick!
        </h2>
        <p class="text-body-1 mb-1">
          You've captured <strong>{{ sessionTotal }}</strong> achievements this
          session.
        </p>
        <p class="text-body-2 text--secondary mb-4">
          That's real career evidence in the bank. Every one of these makes your
          profile stronger and more discoverable.
        </p>
        <v-btn color="success" @click="dismissCelebration" class="mr-2">
          <v-icon left>mdi-plus</v-icon>
          Keep Going
        </v-btn>
        <v-btn
          outlined
          @click="
            showCelebration = false;
            $router.push('/CareerProfile');
          "
        >
          View My Profile
        </v-btn>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import VoiceInput from "../../components/VoiceInput.vue";
import * as GuidedRantApi from "../../api/GuidedRantApi";
import * as SmallWinsApi from "../../api/SmallWinsApi";
import * as ProfileContextApi from "../../api/ProfileContextApi";
import { mapState } from "vuex";
import { mapFields } from "vuex-map-fields";

export default {
  name: "GuidedRantMode",
  components: {
    "voice-input": VoiceInput,
  },
  data: function() {
    return {
      phase: "chat",
      activeMode: "capture",
      messages: [],
      userInput: "",
      selectedJobKey: null,
      existingWinId: null,
      isThinking: false,
      isExtracting: false,
      isSaving: false,
      readyToExtract: false,
      extractedAchievements: [],
      savedCount: 0,
      sessionTotal: 0,
      showCelebration: false,
      celebrationDismissed: false,
      captured: { situation: false, task: false, action: false, result: false },
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      voiceReadbackEnabled: false,
      isSpeaking: false,
      chipsDismissed: false,
      sageMode: null,
      profileContext: null,
      profileLoading: false,
      starItems: [
        { key: "situation", label: "Context / Situation" },
        { key: "task", label: "Task / Responsibility" },
        { key: "action", label: "Action / What You Did" },
        { key: "result", label: "Result / Impact" },
      ],
      gapQueue: [],
      gapQueueIndex: -1,
      gapPhase: null,
      gapUpdatedFields: null,
      gapReviewFields: {
        background: "",
        responsibility: "",
        actionResult: "",
        measurement: "",
        summary: "",
      },
      gapSuggestedSummary: "",
      gapOriginalSummary: "",
      gapSavedCount: 0,
      gapSkippedCount: 0,
      coachPhase: "landing",
      coachMessages: [],
      coachInput: "",
      coachIsThinking: false,
      coachFollowUps: [],
      coachSessionKey: "resumation.sageCoach.session",
      showCoachingNudge: false,
      coachingNudgeDismissed: false,
      coachSuggestionCards: [
        {
          text: "What roles am I best positioned for?",
          icon: "mdi-compass-outline",
          color: "blue",
        },
        {
          text: "Where are my biggest career evidence gaps?",
          icon: "mdi-magnify",
          color: "orange",
        },
        {
          text: "Help me prepare for an interview",
          icon: "mdi-microphone-outline",
          color: "green",
        },
        {
          text: "Coach me on making my achievements more impactful",
          icon: "mdi-arm-flex",
          color: "deep-purple",
        },
        {
          text: "Am I ready for a senior role?",
          icon: "mdi-trending-up",
          color: "teal",
        },
      ],
    };
  },
  computed: {
    ...mapFields("auth", { isPremium: "isPremium" }),
    ...mapState("settings", ["companyViewEnabled"]),
    ...mapState("work", { workDict: "workDict" }),
    jobOptions: function() {
      var jobs = [];
      if (this.workDict) {
        for (var key in this.workDict) {
          var job = this.workDict[key];
          if (job && job.workGeneral) {
            jobs.push({
              key: key,
              label: this.getJobLabel(job),
              isCurrent: job.workGeneral.workingCurrently || false,
            });
          }
        }
      }
      jobs.sort(function(a, b) {
        return (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0);
      });
      return jobs;
    },
    hasJobs: function() {
      return this.jobOptions.length > 0;
    },
    jobOptionsForSelect: function() {
      return this.jobOptions.map(function(j) {
        return { key: j.key, label: (j.isCurrent ? "* " : "") + j.label };
      });
    },
    selectedJobLabel: function() {
      if (
        !this.selectedJobKey ||
        !this.workDict ||
        !this.workDict[this.selectedJobKey]
      )
        return null;
      return this.getJobLabel(this.workDict[this.selectedJobKey]);
    },
    conversationForApi: function() {
      return this.messages.map(function(m) {
        return { role: m.role, content: m.content };
      });
    },
    userMessageCount: function() {
      var count = 0;
      for (var i = 0; i < this.messages.length; i++) {
        if (this.messages[i].role === "user") count++;
      }
      return count;
    },
    capturedCount: function() {
      var count = 0;
      if (this.captured.situation) count++;
      if (this.captured.task) count++;
      if (this.captured.action) count++;
      if (this.captured.result) count++;
      return count;
    },
    statusLabel: function() {
      if (this.userMessageCount === 0) return "Ready to capture";
      if (this.readyToExtract) return "Ready to save!";
      return this.capturedCount + "/4 elements captured";
    },
    gapUserExchangeCount: function() {
      var count = 0;
      for (var i = 0; i < this.messages.length; i++) {
        if (
          this.messages[i].role === "user" &&
          this.messages[i].content !==
            "Please ask me about the missing details for this achievement."
        ) {
          count++;
        }
      }
      return count;
    },
    showActionChips: function() {
      if (this.chipsDismissed) return false;
      if (this.readyToExtract) return true;
      if (this.userMessageCount >= 2) return true;
      if (this.gapPhase === "confirm-save") return true;
      if (
        this.sageMode === "star-gaps" &&
        this.gapPhase === "filling" &&
        this.gapUserExchangeCount >= 2
      )
        return true;
      return false;
    },
    currentGapItem: function() {
      if (
        this.gapQueueIndex >= 0 &&
        this.gapQueueIndex < this.gapQueue.length
      ) {
        return this.gapQueue[this.gapQueueIndex];
      }
      return null;
    },
    currentGapMissingLabels: function() {
      var item = this.currentGapItem;
      if (!item) return "";
      var fields = this.gapUpdatedFields || {};
      var missing = [];
      if (!item.background && !fields.background)
        missing.push("Context/Situation");
      if (!item.responsibility && !fields.responsibility)
        missing.push("Task/Responsibility");
      if (!item.actionResult && !fields.actionResult) missing.push("Action");
      if (!item.measurement && !fields.measurement)
        missing.push("Result/Measurement");
      return missing.join(", ") || "None";
    },
    userInitials: function() {
      var ctx = this.profileContext;
      if (ctx && ctx.name) {
        var first = (ctx.name.firstName || "").charAt(0).toUpperCase();
        var last = (ctx.name.lastName || "").charAt(0).toUpperCase();
        if (first || last) return first + last;
      }
      return "U";
    },
    userAvatarUrl: function() {
      return null;
    },
    coachConversationForApi: function() {
      return this.coachMessages.map(function(m) {
        return { role: m.role, content: m.content };
      });
    },
    credentialKeywordDetected: function() {
      var keywords = [
        "certification",
        "certificate",
        "credential",
        "license",
        "licensed",
        "licensure",
        "accreditation",
        "accredited",
        "pmp",
        "cpa",
        "cissp",
        "aws certified",
        "azure certified",
        "pe license",
        "cfa",
        "cfp",
        "shrm",
        "phr",
        "sphr",
        "ccna",
        "ccnp",
        "itil",
        "six sigma",
        "comptia",
        "security+",
        "network+",
        "capm",
        "scrum master",
        "csm",
        "cism",
        "crisc",
      ];
      for (var i = 0; i < this.messages.length; i++) {
        if (this.messages[i].role === "user") {
          var text = this.messages[i].content.toLowerCase();
          for (var k = 0; k < keywords.length; k++) {
            if (text.indexOf(keywords[k]) >= 0) return true;
          }
        }
      }
      return false;
    },
  },
  watch: {
    "$store.state.resumeId": function(newVal) {
      if (newVal && !this.profileContext && !this.profileLoading) {
        this.loadProfileContext();
      }
    },
    "$route.query.tab": function(newTab) {
      if (newTab === "coach") {
        this.activeMode = "coach";
        if (!this.profileContext && !this.profileLoading) {
          this.loadProfileContext();
        }
      } else if (!newTab) {
        this.activeMode = "capture";
      }
    },
    coachMessages: {
      deep: true,
      handler: function() {
        this.persistCoachSession();
      },
    },
    coachFollowUps: function() {
      this.persistCoachSession();
    },
  },
  mounted: function() {
    // PR-UPGRADE-MODAL: gating unchanged. Sage AI Coach is Plus, so a free
    // user gets the shared upgrade modal and is sent back to a surface that
    // works for them (history or CareerProfile fallback).
    if (!this.isPremium && !this.companyViewEnabled) {
      this.$store.dispatch("upgradeModal/open", {
        featureName: "Sage AI Coach",
        message:
          "Sage coaching is part of Plus, along with the AI tools (tailored resumes, cover letters, Job Fit, interview prep). Capturing wins stays free."
      });
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.replace({ path: "/CareerProfile" });
      }
      return;
    }
    this.loadVoicePreference();
    // Restore any in-progress Sage coach conversation for this browser session
    // so a reload or tab switch continues where the user left off.
    this.restoreCoachSession();
    var self = this;
    var query = this.$route.query || {};
    if (query.tab === "coach") {
      this.activeMode = "coach";
    }
    if (query.linkedTo) {
      this.selectedJobKey = query.linkedTo;
    }
    // PR-GO-DEEPER-PARITY: when an existingWinId is passed, pull up the
    // canonical record from the store by id and refine THAT record in place
    // (write-back below still targets this.existingWinId). We prefer the
    // record's own fields over the query params so we always show the real,
    // current win. If the record isn't loaded yet, fall back to query params
    // so nothing regresses.
    var canonicalWin = null;
    if (query.existingWinId) {
      this.existingWinId = query.existingWinId;
      var swItems =
        (this.$store.state.smallWins &&
          this.$store.state.smallWins.items) ||
        [];
      for (var wi = 0; wi < swItems.length; wi++) {
        if (swItems[wi].id === query.existingWinId) {
          canonicalWin = swItems[wi];
          break;
        }
      }
      if (canonicalWin) {
        var cDraft = canonicalWin.aiDraft || {};
        var cSummary =
          cDraft.summary || canonicalWin.rawInput || canonicalWin.description || "";
        if (!query.accomplishment && cSummary) {
          query.accomplishment = cSummary;
        }
        if (!query.rawInput && canonicalWin.rawInput) {
          query.rawInput = canonicalWin.rawInput;
        }
        if (!query.situation && (cDraft.situation || canonicalWin.context)) {
          query.situation = cDraft.situation || canonicalWin.context;
        }
        if (!query.task && (cDraft.task || canonicalWin.task)) {
          query.task = cDraft.task || canonicalWin.task;
        }
        if (!query.action && (cDraft.action || canonicalWin.action)) {
          query.action = cDraft.action || canonicalWin.action;
        }
        if (!query.result && (cDraft.result || canonicalWin.outcome)) {
          query.result = cDraft.result || canonicalWin.outcome;
        }
        if (!query.linkedTo && canonicalWin.linkedTo) {
          query.linkedTo = canonicalWin.linkedTo;
          this.selectedJobKey = canonicalWin.linkedTo;
        }
      }
    }
    var pendingMode = query.mode || null;
    var gapContext = null;
    if (query.company || query.role) {
      gapContext = { company: query.company || "", role: query.role || "" };
    }
    var hasExistingContext =
      query.situation || query.task || query.action || query.result;
    this.loadProfileContext(function() {
      if (pendingMode && !hasExistingContext) {
        self.startSageMode(pendingMode, gapContext);
        return;
      }
      if (hasExistingContext) {
        // PR-GO-DEEPER-PARITY: when we pulled up a real record by id, name it
        // in the opening message so the user can see we're refining THAT win.
        if (canonicalWin) {
          var refineLabel =
            (canonicalWin.aiDraft && canonicalWin.aiDraft.summary) ||
            canonicalWin.rawInput ||
            canonicalWin.description ||
            "";
          if (refineLabel.length > 140) {
            refineLabel = refineLabel.slice(0, 137) + "...";
          }
          self.addAiMessage(
            'Pulling up your saved win: "' +
              refineLabel +
              '". Let me help you fill in the gaps and make it even stronger.',
          );
        } else {
          self.addAiMessage(
            "Welcome back! I can see you've already started capturing this achievement. Let me help you fill in the gaps and make it even stronger.",
          );
        }
      } else {
        self.addAiMessage(
          "Hey! I'm Sage, your AI Career Coach. Tell me about something you accomplished at work recently — a project, a problem you solved, a goal you hit. I'll help you capture it.",
        );
      }
      if (query.accomplishment) {
        var contextParts = [];
        contextParts.push(query.accomplishment);
        if (query.rawInput && query.rawInput !== query.accomplishment) {
          contextParts.push("Original note: " + query.rawInput);
        }
        if (query.situation) {
          contextParts.push("Context/Situation: " + query.situation);
          self.captured.situation = true;
        }
        if (query.task) {
          contextParts.push("Task: " + query.task);
          self.captured.task = true;
        }
        if (query.action) {
          contextParts.push("Action: " + query.action);
          self.captured.action = true;
        }
        if (query.result) {
          contextParts.push("Result: " + query.result);
          self.captured.result = true;
        }
        var fullText = contextParts.join("\n");
        self.$nextTick(function() {
          self.userInput = fullText;
          self.$nextTick(function() {
            self.sendMessage();
          });
        });
      }
    });
  },
  beforeDestroy: function() {
    this._destroyed = true;
    this.stopSpeaking();
  },
  methods: {
    startStarGapFill: function(filterSource, gapContext) {
      var self = this;
      var source = filterSource || "all";
      var priorityCompany =
        gapContext && gapContext.company
          ? gapContext.company.toLowerCase().trim()
          : "";
      this.sageMode = "star-gaps";
      this.gapFilterSource = source;
      this.messages = [];
      this.gapQueue = [];
      this.gapQueueIndex = -1;
      this.gapPhase = null;
      this.gapUpdatedFields = null;
      this.gapSavedCount = 0;
      this.gapSkippedCount = 0;

      var scanLabel =
        source === "work"
          ? "experience accomplishments"
          : source === "wins"
          ? "captured wins"
          : "achievements";
      this.addAiMessage(
        "Let me scan your " + scanLabel + " for missing details...",
      );

      var loadAndScan = function() {
        var queue = [];

        if (source !== "wins" && self.workDict) {
          for (var key in self.workDict) {
            var workItem = self.workDict[key];
            if (!workItem || !workItem.achievementList) continue;
            var company =
              (workItem.workGeneral && workItem.workGeneral.companyName) ||
              "Unknown";
            var positions = workItem.positions || workItem.workPositions || [];
            var role = positions.length > 0 ? positions[0].title || "" : "";
            var achList =
              workItem.achievementList || workItem.achievements || [];
            for (var i = 0; i < achList.length; i++) {
              var ach = achList[i];
              var text =
                (ach.useCustom ? ach.displayCustom : ach.displayAuto) ||
                ach.displayCustom ||
                ach.displayAuto ||
                "";
              if (!text.trim()) continue;
              var missingCount = 0;
              if (!ach.background || !ach.background.trim()) missingCount++;
              if (!ach.responsibility || !ach.responsibility.trim())
                missingCount++;
              if (!ach.actionResult || !ach.actionResult.trim()) missingCount++;
              if (!ach.measurement || !ach.measurement.trim()) missingCount++;
              if (missingCount > 0) {
                queue.push({
                  source: "work",
                  workKey: key,
                  achIndex: i,
                  achId: ach.id || "",
                  winId: "",
                  text: text,
                  company: company,
                  role: role,
                  background: ach.background || "",
                  responsibility: ach.responsibility || "",
                  actionResult: ach.actionResult || "",
                  measurement: ach.measurement || "",
                  missingCount: missingCount,
                });
              }
            }
          }
        }

        if (source !== "work") {
          var seenWinIds = {};

          var vuexWins =
            (self.$store.state.smallWins &&
              self.$store.state.smallWins.items) ||
            [];
          for (var ev = 0; ev < vuexWins.length; ev++) {
            var eWin = vuexWins[ev];
            if (!eWin || !eWin.id) continue;
            var eDraft = eWin.aiDraft || {};
            var eText =
              eDraft.summary || eWin.rawInput || eWin.description || "";
            if (!eText.trim()) continue;
            var eMissing = 0;
            if (!eDraft.situation && !eDraft.context) eMissing++;
            if (!eDraft.task) eMissing++;
            if (!eDraft.action) eMissing++;
            if (!eDraft.result && !eDraft.outcome) eMissing++;
            if (eMissing > 0 && !eWin.promotedTo) {
              seenWinIds[eWin.id] = true;
              queue.push({
                source: "win",
                workKey: "",
                achIndex: -1,
                achId: "",
                winId: eWin.id,
                text: eText,
                company: "",
                role: "",
                background: eDraft.situation || eDraft.context || "",
                responsibility: eDraft.task || "",
                actionResult: eDraft.action || "",
                measurement: eDraft.result || eDraft.outcome || "",
                missingCount: eMissing,
              });
            }
          }

          if (self.profileContext && self.profileContext.smallWins) {
            var wins = self.profileContext.smallWins;
            for (var w = 0; w < wins.length; w++) {
              var win = wins[w];
              if (win.id && seenWinIds[win.id]) continue;
              var desc = win.description || "";
              if (!desc.trim()) continue;
              var draft = win.aiDraft || {};
              var wMissing = 0;
              if (!draft.situation && !draft.context) wMissing++;
              if (!draft.task) wMissing++;
              if (!draft.action) wMissing++;
              if (!draft.result && !draft.outcome) wMissing++;
              if (wMissing > 0 && !win.promotedTo) {
                queue.push({
                  source: "win",
                  workKey: "",
                  achIndex: -1,
                  achId: "",
                  winId: win.id || "",
                  text: desc,
                  company: "",
                  role: "",
                  background: draft.situation || draft.context || "",
                  responsibility: draft.task || "",
                  actionResult: draft.action || "",
                  measurement: draft.result || draft.outcome || "",
                  missingCount: wMissing,
                });
              }
            }
          }
        }

        if (priorityCompany) {
          queue.sort(function(a, b) {
            var aMatch =
              a.company && a.company.toLowerCase().trim() === priorityCompany
                ? 1
                : 0;
            var bMatch =
              b.company && b.company.toLowerCase().trim() === priorityCompany
                ? 1
                : 0;
            if (aMatch !== bMatch) return bMatch - aMatch;
            return b.missingCount - a.missingCount;
          });
        } else {
          queue.sort(function(a, b) {
            return b.missingCount - a.missingCount;
          });
        }

        self.gapQueue = queue;

        var itemLabel =
          source === "work"
            ? "experience accomplishment"
            : source === "wins"
            ? "captured win"
            : "achievement";
        var itemLabelPlural =
          source === "work"
            ? "experience accomplishments"
            : source === "wins"
            ? "captured wins"
            : "achievements";
        var completeLabel =
          source === "work"
            ? "experience accomplishments"
            : source === "wins"
            ? "wins"
            : "achievements";

        if (queue.length === 0) {
          self.messages = [];
          self.addAiMessage(
            "Great news — all your " +
              completeLabel +
              " look complete! Every one has full STAR evidence. If you want to add new achievements, just start a regular conversation.",
          );
          self.gapPhase = null;
          return;
        }

        self.messages = [];
        var modeTitle =
          source === "work"
            ? "Filling STAR Gaps for Experience Accomplishments"
            : source === "wins"
            ? "Filling STAR Gaps for Wins"
            : "Filling STAR Gaps";
        var intro =
          "**" +
          modeTitle +
          "**\n\nI found " +
          queue.length +
          " " +
          (queue.length > 1 ? itemLabelPlural : itemLabel) +
          " with missing details. ";
        intro +=
          "I'll walk through them one at a time — just answer my questions and I'll fill in the gaps. ";
        intro += "You can skip any item or stop at any time.";
        self.addAiMessage(intro);

        self.gapQueueIndex = 0;
        self.gapPhase = "filling";
        self.startGapItem();
      };

      var ensureDataLoaded = function(cb) {
        var storeLoaded =
          self.$store.state.smallWins && self.$store.state.smallWins.isLoaded;
        var tasks = [];
        if (!self.profileContext) {
          tasks.push(
            new Promise(function(resolve) {
              self.loadProfileContext(resolve);
            }),
          );
        }
        if (!storeLoaded) {
          tasks.push(self.$store.dispatch("smallWins/loadSmallWins"));
        }
        if (tasks.length > 0) {
          Promise.all(tasks)
            .then(cb)
            .catch(cb);
        } else {
          cb();
        }
      };

      ensureDataLoaded(loadAndScan);
    },
    startGapItem: function() {
      var item = this.currentGapItem;
      if (!item) return;

      this.gapUpdatedFields = {
        background: item.background || "",
        responsibility: item.responsibility || "",
        actionResult: item.actionResult || "",
        measurement: item.measurement || "",
      };
      this.gapSuggestedSummary = "";
      if (item.source === "work") {
        this.gapOriginalSummary = item.displayCustom || item.displayAuto || "";
      } else {
        this.gapOriginalSummary = item.text || "";
      }
      this.gapPhase = "filling";
      this.captured = {
        situation: !!item.background,
        task: !!item.responsibility,
        action: !!item.actionResult,
        result: !!item.measurement,
      };

      var contextMsg = "";
      if (item.source === "work" && item.company) {
        var roleStr = item.role ? " (" + item.role + ")" : "";
        contextMsg =
          "Looking at achievement from " +
          item.company +
          roleStr +
          ': "' +
          item.text.substring(0, 100) +
          (item.text.length > 100 ? "..." : "") +
          '"';
      } else {
        contextMsg =
          'Looking at: "' +
          item.text.substring(0, 100) +
          (item.text.length > 100 ? "..." : "") +
          '"';
      }
      this.messages.push({ role: "assistant", content: contextMsg });

      this.messages.push({
        role: "user",
        content:
          "Please ask me about the missing details for this achievement.",
      });
      this.continueConversation();
    },
    skipGapItem: function() {
      this.gapSkippedCount++;
      this.advanceGapQueue();
    },
    stopGapFill: function() {
      this.gapPhase = null;
      this.messages = [];
      var msg = "No problem! ";
      if (this.gapSavedCount > 0) {
        msg +=
          "I updated " +
          this.gapSavedCount +
          " achievement" +
          (this.gapSavedCount > 1 ? "s" : "") +
          ". ";
      }
      msg += "You can come back to fill more gaps anytime.";
      this.addAiMessage(msg);
      this.sageMode = null;
    },
    forceGapSave: function() {
      var item = this.currentGapItem;
      if (!item) return;
      if (!this.gapUpdatedFields) {
        this.gapUpdatedFields = {
          background: item.background || "",
          responsibility: item.responsibility || "",
          actionResult: item.actionResult || "",
          measurement: item.measurement || "",
        };
      }
      this.gapPhase = "confirm-save";
      this.addAiMessage(
        "Ready to save what we have for this achievement. Click 'Review & Save' to review the STAR elements before saving, or 'Skip This One' to move on.",
      );
    },
    enterGapReview: function() {
      var item = this.currentGapItem;
      var fields = this.gapUpdatedFields || {};
      this.gapReviewFields = {
        background: fields.background || item.background || "",
        responsibility: fields.responsibility || item.responsibility || "",
        actionResult: fields.actionResult || item.actionResult || "",
        measurement: fields.measurement || item.measurement || "",
        summary: this.gapSuggestedSummary || this.gapOriginalSummary || "",
      };
      this.gapPhase = "review";
    },
    saveFromReview: function(action) {
      this.gapUpdatedFields = {
        background: this.gapReviewFields.background,
        responsibility: this.gapReviewFields.responsibility,
        actionResult: this.gapReviewFields.actionResult,
        measurement: this.gapReviewFields.measurement,
        summary: this.gapReviewFields.summary,
      };
      this._gapReviewAction = action;
      this.saveGapItem();
    },
    advanceGapQueue: function() {
      if (this._gapReviewAction === "profile") {
        this._gapReviewAction = null;
        this.gapPhase = null;
        this.sageMode = null;
        this.$router.push({ path: "/CareerProfile" }).catch(function() {});
        return;
      }
      this._gapReviewAction = null;

      var nextIdx = this.gapQueueIndex + 1;
      if (nextIdx >= this.gapQueue.length) {
        this.gapPhase = null;
        this.messages = [];
        var summary = "All done! ";
        if (this.gapSavedCount > 0) {
          summary +=
            "I updated " +
            this.gapSavedCount +
            " achievement" +
            (this.gapSavedCount > 1 ? "s" : "") +
            ". ";
        }
        if (this.gapSkippedCount > 0) {
          summary += "(" + this.gapSkippedCount + " skipped.) ";
        }
        summary += "Your career evidence is looking stronger!";
        this.addAiMessage(summary);
        this.sageMode = null;
        return;
      }
      this.gapQueueIndex = nextIdx;
      this.messages = [];
      this.startGapItem();
    },
    saveGapItem: function() {
      var self = this;
      var item = this.currentGapItem;
      if (!item) return;

      var fields = this.gapUpdatedFields;
      if (!fields) {
        fields = {
          background: item.background || "",
          responsibility: item.responsibility || "",
          actionResult: item.actionResult || "",
          measurement: item.measurement || "",
        };
      }

      if (
        !fields.background &&
        !fields.responsibility &&
        !fields.actionResult &&
        !fields.measurement
      ) {
        self.showSnackbar("Nothing new to save for this item.", "warning");
        self.advanceGapQueue();
        return;
      }

      console.log(
        "[Sage Gap-Fill] saveGapItem called, source:",
        item.source,
        "workKey:",
        item.workKey,
        "achIndex:",
        item.achIndex,
      );
      console.log("[Sage Gap-Fill] fields to save:", JSON.stringify(fields));

      this.isSaving = true;

      if (item.source === "work") {
        self.$store
          .dispatch("work/loadFreshWork")
          .then(function() {
            var freshWork = self.workDict[item.workKey];
            var achList =
              freshWork &&
              (freshWork.achievementList || freshWork.achievements || []);
            var ach = achList && achList[item.achIndex];
            console.log(
              "[Sage Gap-Fill] freshWork found:",
              !!freshWork,
              "achList length:",
              achList ? achList.length : 0,
              "ach found:",
              !!ach,
            );
            if (ach) {
              if (fields.background) ach.background = fields.background;
              if (fields.responsibility)
                ach.responsibility = fields.responsibility;
              if (fields.actionResult) ach.actionResult = fields.actionResult;
              if (fields.measurement) ach.measurement = fields.measurement;
              ach.displayAuto = [
                ach.background || "",
                ach.responsibility || "",
                ach.actionResult || "",
                ach.measurement || "",
              ]
                .filter(Boolean)
                .join(" ");
              var summaryBullet =
                fields.summary || self.gapSuggestedSummary || "";
              if (summaryBullet) {
                ach.displayCustom = summaryBullet;
                ach.useCustom = true;
              } else if (!ach.useCustom) {
                ach.displayCustom = ach.displayAuto;
              }
              console.log(
                "[Sage Gap-Fill] achievement updated, displayAuto:",
                ach.displayAuto,
                "displayCustom:",
                ach.displayCustom,
              );
            } else {
              console.warn(
                "[Sage Gap-Fill] Could not find achievement at workKey:",
                item.workKey,
                "achIndex:",
                item.achIndex,
              );
            }

            return self.$store.dispatch("work/saveWork");
          })
          .then(function() {
            self.gapSavedCount++;
            self.isSaving = false;
            self.showSnackbar("Achievement updated!", "success");
            console.log("[Sage Gap-Fill] Work achievement saved successfully");
            ProfileContextApi.ClearProfileContextCache();
            self.advanceGapQueue();
          })
          .catch(function(err) {
            console.error("[Sage Gap-Fill] Save failed:", err);
            self.isSaving = false;
            self.showSnackbar("Failed to save. Try again.", "error");
          });
      } else if (item.source === "win") {
        var winSummary =
          fields.summary || self.gapSuggestedSummary || item.text;
        var hasSituation = !!(fields.background || item.background);
        var hasAction = !!(fields.actionResult || item.actionResult);
        var hasResult = !!(fields.measurement || item.measurement);
        var newParsedType =
          hasSituation && hasAction && hasResult ? "achievement" : "note";
        var updates = {
          aiDraft: {
            situation: fields.background || item.background || "",
            task: fields.responsibility || item.responsibility || "",
            action: fields.actionResult || item.actionResult || "",
            result: fields.measurement || item.measurement || "",
            summary: winSummary,
          },
          parsedType: newParsedType,
          status: newParsedType === "achievement" ? "enriched" : "inferred",
        };

        SmallWinsApi.UpdateSmallWin(item.winId, updates)
          .then(function(result) {
            if (result.success) {
              self.gapSavedCount++;
              self.showSnackbar("Win updated!", "success");
              ProfileContextApi.ClearProfileContextCache();
              self.$store.dispatch("smallWins/loadSmallWins");
            } else {
              self.showSnackbar("Failed to save. Try again.", "error");
            }
            self.isSaving = false;
            self.advanceGapQueue();
          })
          .catch(function() {
            self.isSaving = false;
            self.showSnackbar("Failed to save. Try again.", "error");
          });
      }
    },

    dismissChips: function() {
      this.chipsDismissed = true;
    },
    loadProfileContext: function(callback) {
      var self = this;
      var resumeId = this.$store.state.resumeId;
      if (!resumeId) {
        if (callback) callback();
        return;
      }
      self.profileLoading = true;
      ProfileContextApi.GetProfileContext(resumeId)
        .then(function(result) {
          if (result.success && result.data) {
            self.profileContext = result.data;
          }
          self.profileLoading = false;
          if (callback) callback();
        })
        .catch(function() {
          self.profileLoading = false;
          if (callback) callback();
        });
    },
    buildProfileSummaryText: function() {
      var ctx = this.profileContext;
      if (!ctx) return "";
      var parts = [];
      var name =
        ((ctx.name && ctx.name.firstName) || "") +
        " " +
        ((ctx.name && ctx.name.lastName) || "");
      name = name.trim();
      if (name) parts.push("Name: " + name);
      var work = ctx.workExperience || [];
      if (work.length > 0) {
        var workLines = [];
        for (var i = 0; i < work.length; i++) {
          var job = work[i];
          var title =
            job.positions && job.positions.length > 0
              ? job.positions[0].title
              : "";
          var company = job.company || "";
          var current = job.isCurrent ? " (current)" : "";
          if (title || company)
            workLines.push(
              (title || "Role") + " at " + (company || "Company") + current,
            );
          var achs = job.achievements || [];
          for (var a = 0; a < Math.min(achs.length, 3); a++) {
            var desc =
              achs[a].description ||
              achs[a].displayCustom ||
              achs[a].displayAuto ||
              "";
            if (desc) workLines.push("  - " + desc);
          }
        }
        if (workLines.length > 0)
          parts.push("Work Experience:\n" + workLines.join("\n"));
      }
      var skills = ctx.skills || {};
      var allSkills = [].concat(
        skills.hard || [],
        skills.soft || [],
        skills.technical || [],
      );
      if (allSkills.length > 0) parts.push("Skills: " + allSkills.join(", "));
      var edu = ctx.education || [];
      if (edu.length > 0) {
        var eduLines = [];
        for (var e = 0; e < edu.length; e++) {
          var ed = edu[e];
          var line =
            (ed.degree || "") +
            (ed.fieldOfStudy ? " in " + ed.fieldOfStudy : "") +
            (ed.school ? " from " + ed.school : "");
          if (line.trim()) eduLines.push(line.trim());
        }
        if (eduLines.length > 0)
          parts.push("Education: " + eduLines.join("; "));
      }
      var wins = ctx.smallWins || [];
      if (wins.length > 0) {
        var winLines = [];
        for (var w = 0; w < Math.min(wins.length, 10); w++) {
          var win = wins[w];
          var winDesc = win.description || "";
          if (winDesc)
            winLines.push(
              "- " +
                winDesc +
                (win.eventDate ? " (" + win.eventDate + ")" : ""),
            );
        }
        if (winLines.length > 0)
          parts.push(
            "Recent Wins (" + wins.length + " total):\n" + winLines.join("\n"),
          );
      }
      if (ctx.professionalSummary)
        parts.push("Professional Summary: " + ctx.professionalSummary);
      var certs = ctx.certifications || [];
      if (certs.length > 0) parts.push("Certifications: " + certs.join(", "));
      return parts.join("\n\n");
    },
    getSmallWinsForApi: function() {
      var ctx = this.profileContext;
      if (!ctx || !ctx.smallWins) return [];
      return ctx.smallWins.map(function(w) {
        return {
          id: w.id || "",
          description: w.description || "",
          context: w.context || "",
          action: w.action || "",
          outcome: w.outcome || "",
          eventDate: w.eventDate || "",
        };
      });
    },
    buildProfileGreeting: function(mode) {
      var ctx = this.profileContext;
      if (!ctx) return null;
      var name = ctx.name && ctx.name.firstName ? ctx.name.firstName : "";
      var work = ctx.workExperience || [];
      var currentJob = null;
      for (var i = 0; i < work.length; i++) {
        if (work[i].isCurrent) {
          currentJob = work[i];
          break;
        }
      }
      if (!currentJob && work.length > 0) currentJob = work[0];
      var jobTitle =
        currentJob && currentJob.positions && currentJob.positions.length > 0
          ? currentJob.positions[0].title
          : "";
      var company = currentJob ? currentJob.company : "";
      var skills = ctx.skills || {};
      var topSkills = []
        .concat(skills.hard || [], skills.technical || [])
        .slice(0, 5);
      var wins = ctx.smallWins || [];
      var greeting = name ? "Hey " + name + "! " : "Hey! ";
      if (mode === "review-wins") {
        if (wins.length === 0) {
          return (
            greeting +
            "I don't see any captured wins yet. Let's start by logging one — tell me about something you accomplished recently and I'll help you build it out."
          );
        }
        var winPreview = wins[0].description || "your first win";
        return (
          greeting +
          "I can see you've got " +
          wins.length +
          " win" +
          (wins.length > 1 ? "s" : "") +
          ' captured. Let me take a look — I think "' +
          winPreview +
          '" could be a great one to start with. Want to dig into that one, or pick a different one?'
        );
      } else if (mode === "roles") {
        var roleIntro = "";
        if (jobTitle && company) {
          roleIntro =
            "I can see you're working as " + jobTitle + " at " + company + ". ";
        } else if (jobTitle) {
          roleIntro = "I can see your background as " + jobTitle + ". ";
        }
        var skillMention =
          topSkills.length > 0
            ? "With skills in " + topSkills.slice(0, 3).join(", ") + ", "
            : "";
        return (
          greeting +
          roleIntro +
          skillMention +
          "where are you thinking of taking your career next? I can suggest role types that align with what you've built so far."
        );
      } else if (mode === "strengthen") {
        var score = ctx.completenessScore || 0;
        var weakAreas = [];
        if (!jobTitle) weakAreas.push("current role details");
        if (topSkills.length < 3) weakAreas.push("more skills");
        if (wins.length === 0) weakAreas.push("captured wins");
        var strengthIntro = "Your profile is at " + score + "% completeness. ";
        if (weakAreas.length > 0) {
          strengthIntro +=
            "I'd suggest we focus on " +
            weakAreas.join(" and ") +
            " first — that'll have the biggest impact.";
        } else {
          strengthIntro +=
            "It's looking solid! Let me dig into your achievements and see where we can add more detail to make them shine.";
        }
        return greeting + strengthIntro + " What would you like to work on?";
      } else if (mode === "learn") {
        var learnIntro = "";
        if (jobTitle) {
          learnIntro = "Based on your role as " + jobTitle + ", ";
        }
        return (
          greeting +
          learnIntro +
          "let's figure out what skills or areas would make the biggest difference for your next step. What kind of growth are you most interested in — deeper expertise, leadership, or exploring a new direction?"
        );
      } else if (mode === "update") {
        var lastWinDate = wins.length > 0 ? wins[0].eventDate : null;
        var updateIntro = "";
        if (lastWinDate) {
          updateIntro = "Your last captured win was from " + lastWinDate + ". ";
        }
        if (jobTitle && company) {
          updateIntro +=
            "You're listed as " +
            jobTitle +
            " at " +
            company +
            " — is that still current? ";
        }
        return (
          greeting +
          updateIntro +
          "Let's make sure everything is up to date. Any new projects, skills, or role changes since your last update?"
        );
      }
      return null;
    },
    startSageMode: function(mode, gapContext) {
      if (mode === "star-gaps") {
        this.startStarGapFill("all", gapContext);
        return;
      }
      if (mode === "star-gaps-work") {
        this.startStarGapFill("work", gapContext);
        return;
      }
      if (mode === "star-gaps-wins") {
        this.startStarGapFill("wins", gapContext);
        return;
      }
      this.messages = [];
      this.sageMode = mode;
      var profileGreeting = this.buildProfileGreeting(mode);
      if (profileGreeting) {
        this.addAiMessage(profileGreeting);
      } else {
        var greetings = {
          "review-wins":
            "Hey! Let's review your recent wins and make sure they tell the full story. I'll walk through them and ask follow-up questions to fill in any gaps. Which win would you like to start with, or should I pick one that could use more detail?",
          roles:
            "I'd love to help you explore what types of roles fit your profile. Tell me a bit about where you'd like to take your career — are you looking to grow in your current direction, or are you curious about a shift? I'll look at your skills and experience to suggest what fits.",
          strengthen:
            "Let's strengthen your profile! I can see your career evidence and help you identify areas where adding more detail would make the biggest impact. Want me to look at your weakest areas first, or is there a specific part of your profile you'd like to work on?",
          learn:
            "Great question — let's figure out what skills or areas would have the biggest impact on your career growth. Tell me about your goals or the types of roles you're interested in, and I'll identify development areas based on your current profile.",
          update:
            "Time to freshen things up! Let's walk through your profile and make sure everything is current. Have you changed roles, picked up new skills, or completed any projects recently that aren't captured yet?",
        };
        this.addAiMessage(greetings[mode] || greetings["strengthen"]);
      }
    },
    sendMessage: function() {
      if (!this.userInput.trim() || this.isThinking) return;
      var text = this.userInput.trim();
      this.userInput = "";
      this.chipsDismissed = false;
      this.messages.push({ role: "user", content: text });
      this.scrollToBottom();
      this.detectCoachingMoment(text);
      this.continueConversation();
    },
    detectCoachingMoment: function(text) {
      if (
        this.coachingNudgeDismissed ||
        this.showCoachingNudge ||
        this.activeMode !== "capture"
      )
        return;
      var keywords = [
        "career",
        "should i apply",
        "ready for",
        "what role",
        "interview",
        "next step",
        "gap",
        "promotion",
        "advice",
        "career path",
        "career move",
        "career change",
      ];
      var lower = text.toLowerCase();
      for (var i = 0; i < keywords.length; i++) {
        if (lower.indexOf(keywords[i]) >= 0) {
          this.showCoachingNudge = true;
          return;
        }
      }
    },
    switchToCoachFromNudge: function() {
      this.showCoachingNudge = false;
      this.coachingNudgeDismissed = true;
      var lastUserMsg = "";
      for (var i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].role === "user") {
          lastUserMsg = this.messages[i].content;
          break;
        }
      }
      this.activeMode = "coach";
      if (lastUserMsg) {
        this.startCoachFromCard(lastUserMsg);
      }
    },
    dismissCoachingNudge: function() {
      this.showCoachingNudge = false;
      this.coachingNudgeDismissed = true;
    },
    startCoachFromCard: function(text) {
      this.coachPhase = "chat";
      this.coachMessages = [];
      this.coachFollowUps = [];
      this.coachInput = "";
      this.coachMessages.push({ role: "user", content: text });
      this.callCoachApi(true);
    },
    sendCoachMessage: function() {
      if (!this.coachInput.trim() || this.coachIsThinking) return;
      var text = this.coachInput.trim();
      this.coachInput = "";
      if (this.coachPhase === "landing") {
        this.coachPhase = "chat";
        this.coachMessages = [];
        this.coachFollowUps = [];
      }
      this.coachMessages.push({ role: "user", content: text });
      this.scrollCoachToBottom();
      this.callCoachApi(this.coachMessages.length === 1);
    },
    sendCoachFollowUp: function(text) {
      this.coachFollowUps = [];
      this.coachMessages.push({ role: "user", content: text });
      this.scrollCoachToBottom();
      this.callCoachApi(false);
    },
    callCoachApi: function(isOpening) {
      var self = this;
      this.coachIsThinking = true;
      this.coachFollowUps = [];
      var profileText = this.buildFullProfileContext();
      var conversation = this.coachConversationForApi;
      if (conversation.length > 100) {
        conversation = conversation.slice(conversation.length - 100);
      }
      GuidedRantApi.SageCareerCoach(
        conversation,
        profileText,
        isOpening || false,
      )
        .then(function(response) {
          if (response.success && response.data) {
            var data = response.data;
            if (typeof data === "string") {
              try {
                data = JSON.parse(data);
              } catch (e) {
                /* keep as string */
              }
            }
            var reply = "";
            if (typeof data === "string") {
              reply = data;
            } else {
              reply =
                data.message || data.reply || data.response || data.raw || "";
            }
            if (!reply)
              reply =
                "I'm having trouble forming a response. Could you rephrase that?";
            self.coachMessages.push({ role: "assistant", content: reply });
            var followUps =
              data.suggestedFollowUps || data.suggested_followups || [];
            if (Array.isArray(followUps) && followUps.length > 0) {
              self.coachFollowUps = followUps;
            }
            var exchangeCount = 0;
            for (var i = 0; i < self.coachMessages.length; i++) {
              if (self.coachMessages[i].role === "user") exchangeCount++;
            }
            if (exchangeCount >= 50 && exchangeCount % 10 === 0) {
              self.coachMessages.push({
                role: "assistant",
                content:
                  "We've had a great conversation! For the best experience, consider starting a fresh topic using the **+ New Topic** button. It helps me give you more focused advice.",
              });
            }
          } else {
            var errMsg = (response && response.msg) || "Unknown error";
            console.error("Career Coach API error:", errMsg);
            self.coachMessages.push({
              role: "assistant",
              content:
                "I had trouble processing that — " +
                errMsg +
                ". Could you try again?",
            });
          }
        })
        .catch(function(error) {
          console.error("Career Coach error:", error);
          self.coachMessages.push({
            role: "assistant",
            content:
              "Something went wrong connecting to Sage. Please try again in a moment.",
          });
        })
        .finally(function() {
          self.coachIsThinking = false;
          self.scrollCoachToBottom();
        });
    },
    switchToCoachWithTopic: function(topic) {
      var topicTexts = {
        roles: "What roles am I best positioned for based on my profile?",
        strengthen:
          "Where are the biggest gaps in my profile that I should strengthen?",
        learn: "What skills should I learn next to advance my career?",
      };
      this.activeMode = "coach";
      this.coachPhase = "chat";
      this.coachMessages = [];
      this.coachFollowUps = [];
      this.coachInput = "";
      var text = topicTexts[topic] || topicTexts["strengthen"];
      this.coachMessages.push({ role: "user", content: text });
      this.callCoachApi(true);
    },
    resetCoachToLanding: function() {
      this.coachPhase = "landing";
      this.coachMessages = [];
      this.coachInput = "";
      this.coachFollowUps = [];
      this.coachIsThinking = false;
      this.clearCoachSession();
    },
    persistCoachSession: function() {
      // Save the active coach conversation to sessionStorage so a reload or
      // tab switch continues where the user left off. Session-scoped only:
      // sessionStorage clears on browser close, so no stale cross-day state.
      // Client-side only, no API or token cost.
      try {
        if (typeof window === "undefined" || !window.sessionStorage) return;
        if (this.coachPhase !== "chat" || this.coachMessages.length === 0) {
          window.sessionStorage.removeItem(this.coachSessionKey);
          return;
        }
        var payload = {
          phase: this.coachPhase,
          messages: this.coachMessages,
          followUps: this.coachFollowUps,
        };
        window.sessionStorage.setItem(
          this.coachSessionKey,
          JSON.stringify(payload),
        );
      } catch (e) {
        /* storage unavailable — behave as if not persisted */
      }
    },
    restoreCoachSession: function() {
      // Restore a saved coach conversation on mount if one exists for this
      // session. Returns true if a conversation was restored.
      try {
        if (typeof window === "undefined" || !window.sessionStorage)
          return false;
        var raw = window.sessionStorage.getItem(this.coachSessionKey);
        if (!raw) return false;
        var saved = JSON.parse(raw);
        if (
          !saved ||
          !Array.isArray(saved.messages) ||
          saved.messages.length === 0
        ) {
          return false;
        }
        this.coachMessages = saved.messages;
        this.coachFollowUps = Array.isArray(saved.followUps)
          ? saved.followUps
          : [];
        this.coachPhase = "chat";
        this.coachIsThinking = false;
        return true;
      } catch (e) {
        return false;
      }
    },
    clearCoachSession: function() {
      try {
        if (typeof window === "undefined" || !window.sessionStorage) return;
        window.sessionStorage.removeItem(this.coachSessionKey);
      } catch (e) {
        /* no-op */
      }
    },
    scrollCoachToBottom: function() {
      var self = this;
      this.$nextTick(function() {
        var container = self.$refs.coachChatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    escapeHtml: function(text) {
      if (!text) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    sanitizeCoachUrl: function(url) {
      // Only allow http(s) links. Anything else (javascript:, data:, etc.) is
      // dropped to avoid an XSS hole. The URL has already been HTML-escaped by
      // the time it reaches here.
      if (!url) return "";
      var trimmed = String(url).trim();
      // Decode the &amp; that escapeHtml produced so the test sees the real
      // scheme, but keep using the escaped form in the emitted href.
      var schemeTest = trimmed.replace(/&amp;/g, "&");
      if (/^https?:\/\//i.test(schemeTest)) {
        return trimmed;
      }
      return "";
    },
    renderCoachMarkdown: function(text) {
      if (!text) return "";
      var self = this;
      // ORDER OF OPERATIONS (load-bearing):
      //   1. escapeHtml on the raw text
      //   2. Normalize lines where the model bolded the leading number
      //      (e.g. "**1. Title**: rest" or "**1.** Title"). Without this,
      //      step 3 misses the line because it starts with "<strong>".
      //   3. Detect list STRUCTURE per line and emit <ul>/<ol>/<li> envelope
      //      using the CONTENT only (no inline formatting yet).
      //   4. Run **bold** / *italic* / [link](url) replacements on the
      //      joined html (now inside <li> as well as in surrounding prose).
      //   5. Convert remaining newlines to <br> and clean up <br>s adjacent
      //      to block elements.
      var html = this.escapeHtml(text);
      var lines = html.split("\n");
      // Belt-and-suspenders: rewrite a line whose leading "N." is wrapped
      // in bold so that the list detector below still recognizes it as a
      // numbered item. Conservative patterns only, applied per line.
      //   "**1. Title**: rest"  -> "1. Title: rest"
      //   "**1. Title**"        -> "1. Title"
      //   "**1.** Title"        -> "1. Title"
      // Note the escaped HTML asterisks pass through escapeHtml unchanged
      // (* is not an HTML metacharacter), so the regexes operate on raw **.
      for (var li = 0; li < lines.length; li++) {
        var raw = lines[li];
        // "**1.** Title"  -> "1. Title"  (number+dot fully bolded, then text)
        var m1 = raw.match(/^(\s*)\*\*(\d+)\.\*\*\s+(.+)$/);
        if (m1) {
          lines[li] = m1[1] + m1[2] + ". " + m1[3];
          continue;
        }
        // "**1. Title**: rest"  -> "1. Title: rest"
        // "**1. Title**"        -> "1. Title"
        var m2 = raw.match(/^(\s*)\*\*(\d+)\.\s+(.+?)\*\*\s*(:?)\s*(.*)$/);
        if (m2) {
          var prefix = m2[1] + m2[2] + ". " + m2[3];
          var colon = m2[4] || "";
          var tail = m2[5] || "";
          lines[li] = prefix + colon + (tail ? " " + tail : "");
        }
      }
      // Lists: dash/bullet lines -> <ul>, numbered lines (1. 2. ...) -> <ol>.
      var out = [];
      var listType = null; // "ul" | "ol" | null
      // Running ordinal for numbered items. We stamp it as an explicit
      // <li value="N"> so the number is correct even when a model puts prose
      // between items (which splits the <ol> and would otherwise restart each
      // sub-list at 1, the "1. 1. 1." bug). Reset only when a bullet list
      // starts or the message ends, never for intervening prose.
      var orderedCounter = 0;
      var closeList = function() {
        if (listType) {
          out.push("</" + listType + ">");
          listType = null;
        }
      };
      var typeOfLine = function(line) {
        if (/^\s*[-•]\s+(.+)$/.test(line)) return "ul";
        if (/^\s*\d+\.\s+(.+)$/.test(line)) return "ol";
        return null;
      };
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        var bulletMatch = line.match(/^\s*[-•]\s+(.+)$/);
        var numberMatch = line.match(/^\s*\d+\.\s+(.+)$/);
        if (bulletMatch) {
          if (listType !== "ul") {
            closeList();
            out.push('<ul class="coach-bullets">');
            listType = "ul";
            orderedCounter = 0;
          }
          out.push("<li>" + bulletMatch[1] + "</li>");
        } else if (numberMatch) {
          if (listType !== "ol") {
            closeList();
            out.push('<ol class="coach-numbers">');
            listType = "ol";
          }
          // numberMatch[1] is the text AFTER the literal "N." so the model's
          // own number is never re-emitted. We stamp an explicit running
          // value so numbering stays correct even if prose between items
          // splits the <ol> into separate lists.
          orderedCounter++;
          out.push('<li value="' + orderedCounter + '">' + numberMatch[1] + "</li>");
        } else if (listType && /^\s*$/.test(line)) {
          // Blank line inside a list. Models often separate numbered/bulleted
          // items with a blank line. Closing the list here would start a fresh
          // <ol> for the next item, restarting the counter at 1 every time
          // (the "1. 1. 1." bug). Keep the list open only when the next
          // non-blank line continues the SAME list type; otherwise close it.
          var j = i + 1;
          while (j < lines.length && /^\s*$/.test(lines[j])) j++;
          var nextType = j < lines.length ? typeOfLine(lines[j]) : null;
          if (nextType !== listType) {
            closeList();
            out.push(line);
          }
          // else: swallow the blank line and keep the list contiguous.
        } else {
          closeList();
          out.push(line);
        }
      }
      closeList();
      html = out.join("\n");
      // Inline formatting AFTER the list envelope is built. Running these on
      // the joined html means **bold** inside an <li> still becomes <strong>,
      // and bold spans in surrounding prose still work.
      // Bold: **text**
      html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      // Italic: *text* (avoid matching adjacent **)
      html = html.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");
      // Links: [text](url) -> safe anchor. Text and url are already escaped.
      // Only http/https URLs are allowed; anything else renders as plain text.
      html = html.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, function(
        match,
        label,
        url,
      ) {
        var safeUrl = self.sanitizeCoachUrl(url);
        if (!safeUrl) return label;
        return (
          '<a href="' +
          safeUrl +
          '" target="_blank" rel="noopener noreferrer">' +
          label +
          "</a>"
        );
      });
      // Convert remaining single newlines to <br>
      html = html.replace(/\n/g, "<br>");
      // Clean up <br> immediately after block elements
      html = html.replace(/(<\/(?:ul|ol|li)>)<br>/g, "$1");
      html = html.replace(/<br>(<(?:ul|ol|li))/g, "$1");
      return html;
    },
    buildFullProfileContext: function() {
      var ctx = this.profileContext;
      if (!ctx) return "";
      var parts = [];
      var today = new Date().toISOString().slice(0, 10);
      parts.push("As-of date: " + today);

      var firstName = (ctx.name && ctx.name.firstName) || "";
      var lastName = (ctx.name && ctx.name.lastName) || "";
      var fullName = (firstName + " " + lastName).trim();
      if (fullName) parts.push("Name: " + fullName);

      if (ctx.address) {
        var locParts = [ctx.address.city, ctx.address.state, ctx.address.country].filter(function(x) { return x; });
        if (locParts.length) parts.push("Location: " + locParts.join(", "));
      }

      if (ctx.professionalSummary) parts.push("Professional Summary: " + ctx.professionalSummary);

      var skills = ctx.skills || {};
      if (skills.hard && skills.hard.length) parts.push("Technical/Hard Skills: " + skills.hard.join(", "));
      if (skills.soft && skills.soft.length) parts.push("Leadership & Soft Skills: " + skills.soft.join(", "));
      if (skills.technical && skills.technical.length) parts.push("Technical Tools: " + skills.technical.join(", "));

      if (ctx.adjectives && ctx.adjectives.length) parts.push("Self-Description Adjectives: " + ctx.adjectives.join(", "));
      if (ctx.careerInterests && ctx.careerInterests.length) parts.push("Career Interests: " + ctx.careerInterests.join(", "));
      if (ctx.personalInterests && ctx.personalInterests.length) parts.push("Personal Interests: " + ctx.personalInterests.join(", "));
      if (ctx.languages && ctx.languages.length) parts.push("Languages: " + ctx.languages.join(", "));
      if (ctx.certifications && ctx.certifications.length) parts.push("Licenses & Certifications: " + ctx.certifications.join(", "));

      var work = ctx.workExperience || [];
      if (work.length) {
        var workLines = [];
        var ordered = work.slice().sort(function(a, b) {
          if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
          return (b.endDate || b.startDate || "").localeCompare(a.endDate || a.startDate || "");
        });
        ordered.forEach(function(job) {
          var company = job.company || "Unknown Company";
          var dateRange = (job.startDate || "?") + " – " + (job.isCurrent ? "Present" : (job.endDate || "?"));
          var statusTag = job.isCurrent ? "[CURRENT ROLE]" : "[PAST ROLE]";
          var positions = job.positions || [];
          if (positions.length) {
            positions.forEach(function(p) {
              var title = (p.title || p.role || "").trim() || "Role";
              workLines.push("  - " + statusTag + " " + title + " at " + company + " (" + dateRange + ")");
            });
          } else {
            workLines.push("  - " + statusTag + " Role at " + company + " (" + dateRange + ")");
          }
          var achs = job.achievements || [];
          achs.forEach(function(a) {
            var text = (a.useCustom ? a.displayCustom : a.displayAuto) || a.displayCustom || a.displayAuto || a.description || "";
            if (text) {
              workLines.push("    Accomplishment: " + text);
              if (a.background) workLines.push("      Context: " + a.background);
              if (a.actionResult) workLines.push("      Action & Result: " + a.actionResult);
              if (a.measurement) workLines.push("      Impact: " + a.measurement);
            }
          });
        });
        if (workLines.length) parts.push("Work Experience (most recent first):\n" + workLines.join("\n"));
      }

      var education = ctx.education || [];
      if (education.length) {
        var eduLines = [];
        education.forEach(function(ed) {
          var degree = ed.degree || "";
          var field = ed.fieldOfStudy ? " in " + ed.fieldOfStudy : "";
          var school = ed.school ? " from " + ed.school : "";
          var endDate = ed.endDate || ed.graduationDate || "";
          var statusTag;
          var dateText;
          if (endDate && endDate <= today) {
            statusTag = "[GRADUATED]";
            dateText = endDate.slice(0, 4);
          } else if (endDate) {
            statusTag = "[IN PROGRESS]";
            dateText = "expected " + endDate.slice(0, 4);
          } else if (ed.startDate) {
            statusTag = "[IN PROGRESS]";
            dateText = "started " + ed.startDate.slice(0, 4);
          } else {
            statusTag = "[STATUS UNKNOWN]";
            dateText = "date not specified";
          }
          var line = "  - " + statusTag + " " + (degree + field).trim() + school + " (" + dateText + ")";
          eduLines.push(line);
        });
        parts.push("Education:\n" + eduLines.join("\n"));
      }

      var fp = ctx.fitPreferences || [];
      if (fp.length) {
        var fpLines = fp.map(function(item) {
          var sel = (item.selected || []).join(", ");
          return "  - " + (item.question || item.questionId) + ": " + sel;
        });
        parts.push("Fit & Preferences:\n" + fpLines.join("\n"));
      }
      if (ctx.fitPreferencesNotes) parts.push("Fit Preferences Notes: " + ctx.fitPreferencesNotes);

      var wins = ctx.smallWins || [];
      if (wins.length) {
        var winLines = [];
        for (var w = 0; w < Math.min(wins.length, 15); w++) {
          var win = wins[w];
          var winDesc = win.description || "";
          if (!winDesc) continue;
          winLines.push("  - " + winDesc + (win.eventDate ? " (" + win.eventDate + ")" : ""));
          if (win.context) winLines.push("    Situation: " + win.context);
          if (win.action) winLines.push("    Action: " + win.action);
          if (win.outcome) winLines.push("    Outcome: " + win.outcome);
        }
        if (winLines.length)
          parts.push("Recent Wins (" + wins.length + " total, showing up to 15):\n" + winLines.join("\n"));
      }

      return parts.join("\n\n");
    },
    continueConversation: function() {
      var self = this;
      this.isThinking = true;

      var apiCall;
      if (this.sageMode === "review-wins") {
        apiCall = GuidedRantApi.SageReviewWins(
          this.conversationForApi,
          this.getSmallWinsForApi(),
          this.buildProfileSummaryText(),
        );
      } else if (
        this.sageMode === "roles" ||
        this.sageMode === "strengthen" ||
        this.sageMode === "learn" ||
        this.sageMode === "update"
      ) {
        apiCall = GuidedRantApi.SageProfileAdvisor(
          this.conversationForApi,
          this.buildProfileSummaryText(),
          this.sageMode,
        );
      } else if (this.sageMode === "star-gaps" && this.currentGapItem) {
        var gapItem = this.currentGapItem;
        apiCall = GuidedRantApi.SageStarGapFill(
          this.conversationForApi,
          gapItem.text,
          gapItem.background,
          gapItem.responsibility,
          gapItem.actionResult,
          gapItem.measurement,
          this.buildProfileSummaryText(),
        );
      } else {
        apiCall = GuidedRantApi.ContinueConversation(this.conversationForApi);
      }

      apiCall
        .then(function(response) {
          if (response.success && response.data) {
            var data = response.data;
            var reply = data.reply || data.response || data.raw || "";
            if (!reply && typeof data === "string") {
              reply = data;
            }
            if (!reply) {
              reply =
                "I captured that. Can you tell me a bit more about what happened?";
            }
            self.addAiMessage(reply);

            if (data.captured) {
              if (data.captured.situation) self.captured.situation = true;
              if (data.captured.task) self.captured.task = true;
              if (data.captured.action) self.captured.action = true;
              if (data.captured.result) self.captured.result = true;
            }

            if (data.readyToExtract && self.sageMode !== "star-gaps") {
              self.readyToExtract = true;
            }

            if (self.sageMode === "star-gaps" && data.updatedFields) {
              if (!self.gapUpdatedFields) {
                self.gapUpdatedFields = {
                  background: "",
                  responsibility: "",
                  actionResult: "",
                  measurement: "",
                };
              }
              if (data.updatedFields.background)
                self.gapUpdatedFields.background =
                  data.updatedFields.background;
              if (data.updatedFields.responsibility)
                self.gapUpdatedFields.responsibility =
                  data.updatedFields.responsibility;
              if (data.updatedFields.actionResult)
                self.gapUpdatedFields.actionResult =
                  data.updatedFields.actionResult;
              if (data.updatedFields.measurement)
                self.gapUpdatedFields.measurement =
                  data.updatedFields.measurement;
              var gapItem = self.currentGapItem;
              if (gapItem) {
                if (data.updatedFields.background)
                  gapItem.background = data.updatedFields.background;
                if (data.updatedFields.responsibility)
                  gapItem.responsibility = data.updatedFields.responsibility;
                if (data.updatedFields.actionResult)
                  gapItem.actionResult = data.updatedFields.actionResult;
                if (data.updatedFields.measurement)
                  gapItem.measurement = data.updatedFields.measurement;
              }
              if (data.updatedFields.background) self.captured.situation = true;
              if (data.updatedFields.responsibility) self.captured.task = true;
              if (data.updatedFields.actionResult) self.captured.action = true;
              if (data.updatedFields.measurement) self.captured.result = true;
              console.log(
                "[Sage Gap-Fill] Accumulated updatedFields:",
                JSON.stringify(self.gapUpdatedFields),
              );
            }
            if (data.suggestedSummary && self.sageMode === "star-gaps") {
              self.gapSuggestedSummary = data.suggestedSummary;
            }
            if (data.readyToSave && self.sageMode === "star-gaps") {
              self.gapPhase = "confirm-save";
            }
          } else {
            self.addAiMessage(
              "I had trouble processing that. Could you try again?",
            );
            self.showSnackbar("AI response error. Please try again.", "error");
          }
        })
        .catch(function(error) {
          console.error("Error in conversation:", error);
          self.addAiMessage("Something went wrong. Let's try again.");
          self.showSnackbar("Connection error. Please try again.", "error");
        })
        .finally(function() {
          self.isThinking = false;
        });
    },
    extractAchievements: function() {
      var self = this;
      this.isExtracting = true;

      GuidedRantApi.ExtractAchievements(this.conversationForApi)
        .then(function(response) {
          if (response.success && response.data) {
            var achievements =
              response.data.achievements || response.data || [];
            if (Array.isArray(achievements) && achievements.length > 0) {
              self.extractedAchievements = achievements.map(function(a) {
                return {
                  summary: a.summary || "",
                  situation: a.situation || "",
                  task: a.task || "",
                  action: a.action || "",
                  result: a.result || "",
                  rawInput: a.rawInput || "",
                  status: "inferred",
                };
              });
              self.phase = "review";
            } else {
              self.showSnackbar(
                "No specific achievements found yet. Tell me more details!",
                "warning",
              );
            }
          } else {
            self.showSnackbar(
              "Failed to extract achievements. Please try again.",
              "error",
            );
          }
        })
        .catch(function(error) {
          console.error("Error extracting achievements:", error);
          self.showSnackbar("Error extracting achievements", "error");
        })
        .finally(function() {
          self.isExtracting = false;
        });
    },
    removeAchievement: function(idx) {
      this.extractedAchievements.splice(idx, 1);
    },
    saveAllAchievements: function() {
      var self = this;
      this.isSaving = true;
      var achievements = this.extractedAchievements.slice();
      var successCount = 0;
      var idx = 0;

      function saveNext() {
        if (idx >= achievements.length) {
          ProfileContextApi.ClearProfileContextCache();
          self.$store.dispatch("smallWins/loadSmallWins");
          self.savedCount = successCount;
          self.sessionTotal += successCount;
          self.phase = "done";
          self.isSaving = false;
          self.showSnackbar(successCount + " achievement(s) saved!", "success");
          if (self.sessionTotal >= 3 && !self.celebrationDismissed) {
            self.$nextTick(function() {
              self.showCelebration = true;
            });
          }
          return;
        }
        var ach = achievements[idx];
        var aiDraft = {
          summary: ach.summary,
          situation: ach.situation || "",
          task: ach.task || "",
          action: ach.action || "",
          result: ach.result || "",
          status: "verified",
        };
        var savePromise;
        if (idx === 0 && self.existingWinId) {
          savePromise = SmallWinsApi.UpdateSmallWin(self.existingWinId, {
            rawInput: ach.summary,
            description: ach.summary,
            parsedType: "achievement",
            category: "achievement",
            status: "verified",
            aiDraft: aiDraft,
            context: ach.situation || "",
            task: ach.task || "",
            action: ach.action || "",
            outcome: ach.result || "",
            linkedTo: self.selectedJobKey || "",
          });
        } else {
          savePromise = SmallWinsApi.CreateSmallWin(
            ach.summary,
            "achievement",
            aiDraft,
            "verified",
            self.selectedJobKey,
          );
        }
        savePromise
          .then(function(result) {
            if (result.success) successCount++;
            idx++;
            saveNext();
          })
          .catch(function(error) {
            console.error("Error saving achievement " + idx + ":", error);
            idx++;
            saveNext();
          });
      }

      saveNext();
    },
    startAnother: function() {
      this.phase = "chat";
      this.messages = [];
      this.userInput = "";
      this.selectedJobKey = null;
      this.existingWinId = null;
      this.readyToExtract = false;
      this.extractedAchievements = [];
      this.savedCount = 0;
      this.chipsDismissed = false;
      this.captured = {
        situation: false,
        task: false,
        action: false,
        result: false,
      };
      this.addAiMessage(
        "Great, let's capture another one! What else have you accomplished?",
      );
    },
    dismissCelebration: function() {
      this.showCelebration = false;
      this.celebrationDismissed = true;
      this.startAnother();
    },
    addAiMessage: function(text) {
      this.messages.push({ role: "assistant", content: text });
      this.speakText(text);
      this.scrollToBottom();
    },
    scrollToBottom: function() {
      var self = this;
      this.$nextTick(function() {
        var container = self.$refs.chatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    selectJob: function(jobKey) {
      this.selectedJobKey = this.selectedJobKey === jobKey ? null : jobKey;
    },
    getJobLabel: function(job) {
      var title =
        (job.workRole &&
          job.workRole.positions &&
          job.workRole.positions[0] &&
          job.workRole.positions[0].title) ||
        (job.workGeneral && job.workGeneral.display) ||
        "Role";
      var company = (job.workGeneral && job.workGeneral.companyName) || "";
      return company ? title + " @ " + company : title;
    },
    showSnackbar: function(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || "success";
      this.snackbar = true;
    },
    loadVoicePreference: function() {
      try {
        var saved = localStorage.getItem("guidedRant_voiceEnabled");
        this.voiceReadbackEnabled = saved === "true";
      } catch (e) {
        // ignore
      }
    },
    saveVoicePreference: function() {
      try {
        localStorage.setItem(
          "guidedRant_voiceEnabled",
          String(this.voiceReadbackEnabled),
        );
        if (this.voiceReadbackEnabled) {
          this.showSnackbar("Sage voice enabled", "info");
        }
      } catch (e) {
        // ignore
      }
    },
    speakText: function(text) {
      if (!this.voiceReadbackEnabled || !text) return;
      this.stopSpeaking();
      this._speechGeneration = (this._speechGeneration || 0) + 1;
      this.speakTextOpenAi(text, this._speechGeneration);
    },
    speakTextOpenAi: function(text, generation) {
      var self = this;
      this.isSpeaking = true;
      var controller = new AbortController();
      this._currentAbortController = controller;
      fetch("/api/TextToSpeech", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: text.substring(0, 4096), voice: "nova" }),
        signal: controller.signal,
      })
        .then(function(response) {
          return response.json();
        })
        .then(function(result) {
          if (self._speechGeneration !== generation || self._destroyed) return;
          if (result.success && result.data && result.data.audio) {
            var audioBytes = Uint8Array.from(atob(result.data.audio), function(
              c,
            ) {
              return c.charCodeAt(0);
            });
            var blob = new Blob([audioBytes], {
              type: result.data.contentType || "audio/mpeg",
            });
            var url = URL.createObjectURL(blob);
            self._currentAudio = new Audio(url);
            self._currentAudio.onended = function() {
              self.isSpeaking = false;
              URL.revokeObjectURL(url);
            };
            self._currentAudio.onerror = function() {
              self.isSpeaking = false;
              URL.revokeObjectURL(url);
              if (self._speechGeneration === generation && !self._destroyed) {
                self.speakTextBrowserFallback(text, generation);
              }
            };
            self._currentAudio.play();
          } else {
            if (self._speechGeneration === generation && !self._destroyed) {
              self.speakTextBrowserFallback(text, generation);
            }
          }
        })
        .catch(function(e) {
          if (e.name === "AbortError") return;
          console.warn("OpenAI TTS failed, using browser voice:", e);
          if (self._speechGeneration === generation && !self._destroyed) {
            self.speakTextBrowserFallback(text, generation);
          }
        });
    },
    speakTextBrowserFallback: function(text, generation) {
      var self = this;
      if (self._destroyed) return;
      if (generation !== undefined && self._speechGeneration !== generation)
        return;
      if ("speechSynthesis" in window) {
        var utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        var voices = window.speechSynthesis.getVoices();
        for (var i = 0; i < voices.length; i++) {
          if (
            voices[i].name &&
            voices[i].name.toLowerCase().indexOf("female") > -1
          ) {
            utterance.voice = voices[i];
            break;
          }
        }
        utterance.onstart = function() {
          if (!self._destroyed) self.isSpeaking = true;
        };
        utterance.onend = function() {
          if (!self._destroyed) self.isSpeaking = false;
        };
        utterance.onerror = function() {
          if (!self._destroyed) self.isSpeaking = false;
        };
        window.speechSynthesis.speak(utterance);
      } else {
        self.isSpeaking = false;
      }
    },
    stopSpeaking: function() {
      if (this._currentAbortController) {
        try {
          this._currentAbortController.abort();
        } catch (e) {
          /* ignore */
        }
        this._currentAbortController = null;
      }
      if (this._currentAudio) {
        this._currentAudio.pause();
        this._currentAudio.onended = null;
        this._currentAudio.onerror = null;
        this._currentAudio = null;
      }
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      this.isSpeaking = false;
    },
  },
};
</script>

<style scoped>
.chat-panel-card {
  display: flex;
  flex-direction: column;
  /* PR-INTERVIEW-STUDIO follow-up: stick the chat panel to the viewport so
     the input stays visible even when a tall sidebar (Update mode's STAR
     progress + Link to Role + voice toggle) pushes the page down. Carlos's
     complaint: "I have to scroll the entire page down just to use sage for
     a long time." With position:sticky, the panel follows the viewport as
     the user scrolls past the sidebar. */
  position: sticky;
  top: 76px;
  /* Cap the whole panel so the page doesn't get longer than the viewport. */
  max-height: calc(100vh - 92px);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 280px;
  /* Viewport-relative — scales with screen size. Old hard cap of 500px was
     too tall on shorter laptops (input fell below the fold on Update mode
     where a tall sidebar already takes the top of the viewport). 55vh on a
     1080p screen = ~594px (bigger than before). On 720p = ~396px (smaller,
     but the input stays visible). */
  max-height: 55vh;
}

.chat-bubble-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.bubble-left {
  justify-content: flex-start;
}

.bubble-right {
  justify-content: flex-end;
}

.chat-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--v-primary-base, #1e3a5f);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  flex-shrink: 0;
}

.sage-avatar {
  color: white;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
}

.chat-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.bubble-ai {
  background: var(--v-background-lighten1, #f3f4f6);
  color: var(--v-text-base, #1f2937);
  border-bottom-left-radius: 4px;
}

.theme--dark .bubble-ai {
  background: #2d2d2d;
  color: #e0e0e0;
}

.bubble-user {
  background: var(--v-primary-base, #1e3a5f);
  color: white;
  border-bottom-right-radius: 4px;
}

.chat-input-bar {
  display: flex;
  align-items: flex-end;
}

.chat-text-input {
  flex: 1;
}

.chat-text-input >>> .v-textarea textarea {
  max-height: 120px;
  overflow-y: auto !important;
}

.action-chips-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 8px 16px 4px;
}

.action-chip {
  cursor: pointer;
  font-weight: 500;
}

.achievement-review-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-left: 4px solid var(--v-primary-base, #1e3a5f);
  border-radius: 8px;
}

.theme--dark .achievement-review-card {
  background: #2d2d2d;
  border-color: #424242;
  border-left-color: var(--v-primary-base, #1e3a5f);
}

.achievement-review-card >>> .v-textarea textarea {
  max-height: 200px;
  overflow-y: auto !important;
}

.gap-context-banner {
  background: #fff7ed;
  border-bottom: 2px solid #f59e0b;
}

.theme--dark .gap-context-banner {
  background: #3e2e1a;
}

.opacity-80 {
  opacity: 0.8;
}

.celebration-card {
  border-top: 4px solid #10b981;
}

.celebration-emoji {
  font-size: 64px;
  line-height: 1;
  animation: celebrationBounce 0.6s ease-out;
}

@keyframes celebrationBounce {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.mode-toggle {
  border-radius: 8px;
}

.coach-landing {
  padding: 24px 0;
}

.coach-avatar-container {
  display: flex;
  justify-content: center;
}

.coach-user-initials {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  font-size: 24px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coach-user-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
}

.coach-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.coach-suggestion-card {
  transition: all 0.2s ease;
  border-radius: 12px;
}

.coach-suggestion-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.15);
  border-color: #7c3aed;
}

.coach-avatar-bubble {
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%) !important;
}

.coach-bubble-ai {
  background: #f5f3ff !important;
  color: #1f2937 !important;
}

.theme--dark .coach-bubble-ai {
  background: #2d2040 !important;
  color: #e0e0e0 !important;
}

.coach-bubble-ai strong {
  font-weight: 600;
}

.coach-bubble-ai .coach-bullets {
  margin: 6px 0;
  padding-left: 18px;
}

.coach-bubble-ai .coach-bullets li {
  margin-bottom: 4px;
  line-height: 1.45;
}

.coach-bubble-ai .coach-numbers {
  margin: 6px 0;
  padding-left: 22px;
  list-style: decimal;
}

.coach-bubble-ai .coach-numbers li {
  margin-bottom: 4px;
  line-height: 1.45;
}

.coach-bubble-user {
  background: #7c3aed !important;
  color: white !important;
}

.coach-followups-row {
  display: flex;
  flex-wrap: wrap;
  padding: 8px 16px 4px;
}

.coach-fade-enter-active,
.coach-fade-leave-active {
  transition: opacity 0.3s ease;
}

.coach-fade-enter,
.coach-fade-leave-to {
  opacity: 0;
}

.heading-primary {
  color: #1e3a5f;
}

.theme--dark .heading-primary {
  color: #90caf9;
}

.coaching-nudge-card {
  border-left: 4px solid #7c3aed;
  background: #f5f3ff;
}

.theme--dark .coaching-nudge-card {
  background: #2d2040;
}

.coaching-nudge-banner {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
